import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useParams, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  Box,
  Typography,
  Alert,
  Paper,
  Grid,
  TextField,
  Button,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  Snackbar,
  CircularProgress,
  Pagination,
  PaginationItem,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  Autocomplete,
  MenuList,
  ClickAwayListener,
  Popper,
} from '@mui/material';
import {
  Add as AddIcon,
  AddCircle as AddOtherIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CloudUpload as UploadIcon,
  Visibility as ViewIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  QuestionAnswer as VerifyIcon,
  QrCode as QrCodeIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import TestingLaboratoryService from '../../../services/testinglaboratoryService';
import UnfreezeApiService from '../../../services/UnfreezeApiService';
import { parseEncryptedQuery, decryptText, decryptAppId, encryptText } from '../../../utils/encryption';
import S3Service from '../../../services/s3Service';
import QRCode from 'react-qr-code';
import jsPDF from 'jspdf';
import * as QRCodeLib from 'qrcode';
import { 
    insertDANC, 
    updateDANCBySourceId, 
    getDANC,
} from '../../../services/apiCalibration';
import {getScrutinyRemarks, getClauseNo} from '../../../services/api';
import s3Utils from '../../../utils/s3Utils';
import { set } from 'react-hook-form';
import { date } from 'yup';
import axios from 'axios'

const MedicalEquipmentSection = ({ data, onUpdate, fields = [], sectionType }) => {
  const [searchParams] = useSearchParams();
  const { orgId: urlOrgId } = useParams();
  const location = useLocation();
  
  // Get today's date in YYYY-MM-DD format for date inputs
  const todayDate = new Date().toISOString().split('T')[0];
  
  // Sample data for existing equipments - will be replaced with API data
  const [existingEquipments, setExistingEquipments] = useState([]);
  const [existingEquipmentsLoading, setExistingEquipmentsLoading] = useState(false);
  
  // Unfreeze functionality state
  const [unfreezeRecords, setUnfreezeRecords] = useState([]);

  
  // Function to get orgId from URL query parameters with decryption
  const getOrgIdFromQuery = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedOrgId = searchParams.get('orgId');
    if (encryptedOrgId) {
      try {
        const decryptedOrgId = decryptText(encryptedOrgId);
        return decryptedOrgId;
      } catch (error) {
        return encryptedOrgId; // Return original if decryption fails
      }
    }
    return null;
  }, [location.search]);

  // Function to get appId from URL query parameters with decryption
  const getAppIdFromQuery = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get('appId');
    if (encryptedAppId) {
      try {
        const decryptedAppId = decryptAppId(encryptedAppId);
        return decryptedAppId;
      } catch (error) {
        return encryptedAppId; // Return original if decryption fails
      }
    }
    return null;
  }, [location.search]);

  // Function to get orgId with priority: isSubUser check > URL query (encrypted) > URL params > null
  const getOrgId = useCallback(() => {
   

    // First check URL query parameters (encrypted) - highest priority
    const queryOrgId = getOrgIdFromQuery();
    if (queryOrgId) {
      return queryOrgId;
    }

    // Then check URL parameters (unencrypted)
    if (urlOrgId) {
      return urlOrgId;
    }

    // No fallback - orgId must be provided via URL
    return null;
  }, [getOrgIdFromQuery, urlOrgId]);

  // Function to get appId with priority: isSubUser check > URL query (encrypted) only
  const getAppId = useCallback(() => {
   
    // Only check URL query parameters (encrypted)
    const queryAppId = getAppIdFromQuery();
    if (queryAppId) {
      return queryAppId;
    }

    // No fallback - appId must be provided via URL query parameters
    return null;
  }, [getAppIdFromQuery]);

  // Function to get assessmentId from URL query parameters with decryption
  const getAssessmentIdFromQuery = () => {
    try {
      const params = new URLSearchParams(location.search);
      const encryptedAssessmentId = params.get('assessmentId');
      if (encryptedAssessmentId) {
        const decryptedAssessmentId = decryptText(encryptedAssessmentId);
        return decryptedAssessmentId;
      }
      return null;
    } catch (error) {
      console.error('Error decrypting assessmentId:', error);
      return null;
    }
  };

  // Function to get assessorId from URL query parameters with decryption
  const getAssessorIdFromQuery = () => {
    try {
      const params = new URLSearchParams(location.search);
      const encryptedAssessorId = params.get('assessorId');
      if (encryptedAssessorId) {
        const decryptedAssessorId = decryptText(encryptedAssessorId);
        return decryptedAssessorId;
      }
      return null;
    } catch (error) {
      console.error('Error decrypting assessorId:', error);
      return null;
    }
  };

  const getAssessmentId = () => {
    return getAssessmentIdFromQuery();
  };

  const getAssessorId = () => {
    return getAssessorIdFromQuery();
  };

  // Get assessmentId and assessorId for userType 6 (Assessment)
  const assessmentId = getAssessmentId();
  const assessorId = getAssessorId();
  
  // Memoize QR code URLs to prevent infinite re-renders
  const qrCodeUrls = useMemo(() => {
    const urls = {};
    const appId = getAppId();
    const orgId = getOrgId();
    
    if (!appId) {
      console.warn('Missing appId for QR code generation');
      return urls;
    }

    // Generate URLs for existing equipments
    existingEquipments.forEach(equipment => {
      const equipmentId = equipment.Id;
      if (equipmentId) {
        try {
          
          const qrUrl = `${equipmentId}~${orgId}`;
          
          urls[equipmentId] = qrUrl;
        } catch (error) {
          console.error('Error generating QR code URL for equipment:', equipmentId, error);
          urls[equipmentId] = null;
        }
      }
    });

    return urls;
  }, [getOrgId, existingEquipments]);

  // Generate QR code URL with encrypted appId and equipmentId (fallback function)
  const generateQRCodeUrl = useCallback((equipmentId) => {
    return qrCodeUrls[equipmentId] || null;
  }, [qrCodeUrls]);

  // Utility function to format date without timezone
  const formatDateWithoutTimezone = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'N/A';
    
    try {
      // If it's already a simple date string (YYYY-MM-DD), convert to dd/mm/yyyy
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
      }
      
      // If it contains timezone info, extract date part and format as dd/mm/yyyy
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid
      
      // Format as dd/mm/yyyy
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateString; // Return original if any error
    }
  };

  // Utility function to format date for HTML input fields (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString || dateString === 'N/A') return '';
    
    try {
      // Debug log to see what we're working with
      console.log('formatDateForInput input:', dateString);
      
      // If it's already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        console.log('Already in YYYY-MM-DD format:', dateString);
        return dateString;
      }
      
      // If it's in dd/mm/yyyy format, convert to YYYY-MM-DD
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
        const [day, month, year] = dateString.split('/');
        const result = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        console.log('Converted dd/mm/yyyy to:', result);
        return result;
      }
      
      // If it contains timezone info or other format, parse and format as YYYY-MM-DD
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.log('Invalid date:', dateString);
        return ''; // Return empty if invalid
      }
      
      // Format as YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const result = `${year}-${month}-${day}`;
      console.log('Converted ISO date to:', result);
      return result;
    } catch (error) {
      console.error('Error in formatDateForInput:', error);
      return ''; // Return empty if any error
    }
  };
  
  // Get URL parameters
  const organizationId = searchParams.get('organizationId');
  const appId = searchParams.get('appId');
  // Note: disciplineId is now obtained from encrypted params in useEffect
  const [activeTab, setActiveTab] = useState(0);
  // const [modalOpen, setModalOpen] = useState(false); // Removed - equipment master now in Tab 1
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [uploadFile, setUploadFile] = useState(null);
  
  // Bulk Upload Preview State
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [previewHeaders, setPreviewHeaders] = useState([]);
  const [previewFileName, setPreviewFileName] = useState('');
  
  // Preview Edit State
  const [editedPreviewData, setEditedPreviewData] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  
  // Equipment Master Data State
  const [equipmentMasterData, setEquipmentMasterData] = useState([]);
  const [equipmentMasterLoading, setEquipmentMasterLoading] = useState(false);
  const [selectedEquipments, setSelectedEquipments] = useState([]);
  const [selectedEquipmentData, setSelectedEquipmentData] = useState([]); // Store complete data for selected equipments across all pages
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(50);

  // All Equipment Data for Dropdown (without pagination)
  const [allEquipmentData, setAllEquipmentData] = useState([]);
  const [allEquipmentLoading, setAllEquipmentLoading] = useState(false);
  
  // Edit Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editingEquipmentId, setEditingEquipmentId] = useState(null);
  const [editReadOnlyData, setEditReadOnlyData] = useState({});
  
  // Add Other Equipment Modal State
  const [addOtherModalOpen, setAddOtherModalOpen] = useState(false);
  const [otherEquipmentForm, setOtherEquipmentForm] = useState({
    equipmentId: '',
    equipmentName: '',
    manufacturerName: '',
    uid: '',
    serialNo: '',
    model: '',
    yearOfMake: new Date().getFullYear(),
    receiptDate: '',
    dateOfInstallation: '',
    limitOfDetection: '',
    dateOfLastCalibration: '',
    calibrationDueOn: '',
    calibratedBy: 'inhouse', // 'inhouse' or 'external'
    traceability: '',
    calibrationAgencyName: ''
  });
  const [otherEquipmentFiles, setOtherEquipmentFiles] = useState({
    iqFile: null,
    oqFile: null,
    pqFile: null
  });
  const [otherEquipmentLoading, setOtherEquipmentLoading] = useState(false);
  
  // Equipment Dropdown Data State
  const [equipmentDropdownData, setEquipmentDropdownData] = useState([]);
  const [equipmentDropdownLoading, setEquipmentDropdownLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  
  // Multiple delete state
  const [selectedForDelete, setSelectedForDelete] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // 'single' or 'multiple'
  const [singleDeleteId, setSingleDeleteId] = useState(null);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Application Stage and Adequacy Check States
  const [applicationStage, setApplicationStage] = useState(null);
  const [adequacyChecks, setAdequacyChecks] = useState({});
  
  // DA-NC Integration States
  const [dancRecords, setDancRecords] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [currentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  });
  const [currentUserId] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.userId || user.id || localStorage.getItem('userId') || '';
    } catch {
      return localStorage.getItem('userId') || '';
    }
  });
  
  // Bulk Adequacy Check States
  const [bulkAdequacyModalOpen, setBulkAdequacyModalOpen] = useState(false);
  const [selectedAdequacyEquipments, setSelectedAdequacyEquipments] = useState([]);
  const [bulkAdequacyStatus, setBulkAdequacyStatus] = useState('');
  const [bulkAdequacyRemarks, setBulkAdequacyRemarks] = useState('');
  const [bulkAdequacyLoading, setBulkAdequacyLoading] = useState(false);
  
  // Bulk Adequacy Scrutiny Remarks States
  const [bulkScrutinyRemarks, setBulkScrutinyRemarks] = useState([]);
  const [bulkLoadingRemarks, setBulkLoadingRemarks] = useState(false);
  
  // Checkbox dropdown states for bulk adequacy
  const [selectedBulkRemarksOptions, setSelectedBulkRemarksOptions] = useState([]);
  const [isBulkCheckboxDropdownOpen, setIsBulkCheckboxDropdownOpen] = useState(false);
  
  // Clause Number states
  const [clauseNo, setClauseNo] = useState([]);
  const [selectedClause, setSelectedClause] = useState(null);

  const [subGroups, setSubGroups] = useState([]);
  const [subGroupsLoading, setSubGroupsLoading] = useState(false);
  const [selectedSubGroup, setSelectedSubGroup] = useState('');
  
  
  // PDF Download State
  const [pdfDownloading, setPdfDownloading] = useState(false);
  
  const [formData, setFormData] = useState({
    disciplineId: '',
    disciplineName: '',
    groupId: '',
    groupName: '',
    appId: '',
    orgId: '',
    ...data
  });

  // Instructions state
  const [instructions, setInstructions] = useState([]);
  const [instructionsLoading, setInstructionsLoading] = useState(false);

  // DA-NC Helper Functions
  const getEquipmentStatus = (equipmentId) => {
    const adequacyData = adequacyChecks[equipmentId];
    const dancRecord = dancRecords.find(record => 
      record.sourceid && parseInt(record.sourceid) === parseInt(equipmentId)
    );
    
    return {
      status: adequacyData?.status || 'Not Assessed',
      remarks: adequacyData?.remarks || dancRecord?.Observation || '',
      ClauseNo: dancRecord?.ClauseNo || null
    };
  };

  const isValidEquipmentSelection = (currentSelection, newEquipmentId, isAdding) => {
    // Business rule: Cannot select both adequate and inadequate equipments in same batch
    const updatedSelection = isAdding 
      ? [...currentSelection, newEquipmentId]
      : currentSelection.filter(id => id !== newEquipmentId);
    
    const statuses = updatedSelection.map(id => getEquipmentStatus(id).status);
    const hasAdequate = statuses.includes('Adequate');
    const hasInadequate = statuses.includes('Inadequate');
    
    return !(hasAdequate && hasInadequate);
  };
   const userTypeId = localStorage.getItem('userTypeId');

  // Load DA-NC data function
  const loadDANCData = useCallback(async () => {
    if (!formData.orgId || !formData.appId) return;
    
    try {
      // Determine expected NCtype based on userTypeId
      let expectedNCType = 'Document Review'; // Default
      
      if (userTypeId === '4') {
        expectedNCType = 'Document Review';
      } else if (userTypeId === '6') {
        expectedNCType = 'Assessment';
      }
      
      // Get all DA NC records for this org and app
      const response = await getDANC({
        orgid: formData.orgId,
        appid: formData.appId,
        assessorId: assessorId,
        isactive: 1
      });
      
      if (response.data.success && response.data.data && response.data.data.length > 0) {
        // Filter Records for Equipment section and by NCtype
        const equipmentDANCRecords = response.data.data.filter(record => 
          (record.SectionName === 'Equipment' || record.SectionName === 'Equipment Details') &&
          record.NCtype === expectedNCType
        );
        
        setDancRecords(equipmentDANCRecords);
        
        // Create adequacy checks from DA NC data
        const dancAdequacyChecks = {};
        
        equipmentDANCRecords.forEach(record => {
          let sectionKey = record.sourceid; // Use sourceid as the key for equipment
          
          if (sectionKey) {
            dancAdequacyChecks[sectionKey] = {
              status: record.InformationStatus || '',
              remarks: record.InformationProvided || record.Observation || '',
              isSubmitted: false, // Allow users to modify and resubmit
              submittedAt: record.updatedate || record.Insertdate,
              dancRecord: record // Store original record for reference
            };
          }
        });
        
        // Merge with existing adequacy checks
        setAdequacyChecks(prev => {
          const merged = {
            ...prev,
            ...dancAdequacyChecks
          };
          return merged;
        });
        
        showNotification(`${equipmentDANCRecords.length} equipment Records loaded and bound to adequacy checks`, 'success');
      } else {
        setDancRecords([]);
      }
    } catch (error) {
      console.error('Error loading equipment DA-NC data:', error);
      showNotification('Error loading DA-NC data: ' + error.message, 'error');
    }
  }, [formData.orgId, formData.appId]);

// Fetch clause numbers from API
       const fetchClauseNo = useCallback(async () => {
           try {
               console.log('Fetching clause numbers for Equipment section...');
   
               const clauseResponse = await getClauseNo({ standard: '2022', feild: 'Medical' });
               console.log('Clause API response:', clauseResponse);
   
               if (clauseResponse.data?.success && clauseResponse.data?.data) {
                   setClauseNo(clauseResponse.data.data);
                   console.log('Loaded clause numbers:', clauseResponse.data.data.length);
                   console.log('First few clause objects:', clauseResponse.data.data.slice(0, 3));
               } else {
                   console.warn('No clause numbers found or API returned success: false');
                   setClauseNo([]);
               }
           } catch (error) {
               console.error('Error fetching clause numbers:', error);
               setClauseNo([]);
           }
       }, []);

  // Fetch instructions from API
  useEffect(() => {
    const fetchInstructions = async () => {
      try {
        setInstructionsLoading(true);
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';
        const response = await fetch(`${API_BASE_URL}/instructions?sectionName=Equipment&labTypeId=1`);
        const data = await response.json();
        
        if (data.success && data.data.instructions) {
          // Sort instructions by orderNo
          const sortedInstructions = data.data.instructions.sort((a, b) => a.orderNo - b.orderNo);
          setInstructions(sortedInstructions);
        }
      } catch (error) {
        console.error('Error fetching instructions:', error);
        // Fallback to default instructions if API fails
        setInstructions([]);
      } finally {
        setInstructionsLoading(false);
      }
    };

    fetchInstructions();
  }, []);

  // Fetch clause numbers on component mount
  useEffect(() => {
    fetchClauseNo();
  }, [fetchClauseNo]);

   useEffect(() => {
    if (formData.disciplineId && formData.groupId) {
      fetchSubGroups();
    }
  }, [formData.disciplineId, formData.groupId]);
  
    const fetchSubGroups = useCallback(async () => {
    const labTypeId = localStorage.getItem('labTypeId') || '1';
    if (!formData.disciplineId || !formData.groupId) return;
  
    setSubGroupsLoading(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';
      const response = await axios.get(`${API_BASE_URL}/admin-master/subgroups`, {
        params: {
          labTypeId,
          disciplineId: formData.disciplineId,
          groupId: formData.groupId,
        },
      });
  
      if (response.data.status === 'success') {
        setSubGroups(response.data.data || []);
      } else {
        setSubGroups([]);
      }
    } catch (error) {
      console.error('Error fetching sub-groups:', error);
      setSubGroups([]);
    } finally {
      setSubGroupsLoading(false);
    }
  }, [formData.disciplineId, formData.groupId]);

  // Extract parameters from URL and localStorage
  useEffect(() => {
    // Parse encrypted query parameters
    const { disciplineId, disciplineName, groupId, groupName, tab } = parseEncryptedQuery(searchParams);
    
    // Get appId and orgId from encrypted query parameters
    const currentAppId = getAppId();
    const currentOrgId = getOrgId();

    // If tab parameter is provided, set it directly (for "View Equipment" navigation)
    if (tab !== null && tab >= 0 && tab <= 3) {
      setActiveTab(tab);
    }

    setFormData(prev => ({
      ...prev,
      disciplineId: disciplineId || '',
      disciplineName: disciplineName || '',
      groupId: groupId || '',
      groupName: groupName || '',
      appId: currentAppId,
      orgId: currentOrgId,
    }));
  }, [searchParams, getAppId, getOrgId]);

  // Note: Tab index is now set directly when URL parameters are parsed
  // No need for applicationStage-based adjustment since tabs are always available

  // Load DA-NC data when form data is available
  useEffect(() => {
    if (formData.orgId && formData.appId) {
      loadDANCData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.orgId, formData.appId, refreshTrigger]);

   const getApplicationStage = async (appId) => {
    try {
      if (!appId) return;
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/laboratory/application-stage/${appId}`);
      const result = await response.json();
      
      if (result.success) {
        setApplicationStage(result.stage);
      } else {
        setApplicationStage(1); // Default to 1 for demo purposes
      }
    } catch (error) {
      console.error('Error fetching application stage:', error);
      setApplicationStage(1); // Default to 1 for demo purposes
    }
  };
  useEffect(() => {
    if (onUpdate) {
      onUpdate(formData);
    }
  }, [formData, onUpdate]);

  
  // Utility function to check if section is unfrozen
  const isSectionUnfrozen = useCallback((sectionName) => {
    const appId = getAppId();
    const organizationId = getOrgId();
    
    const currentDate = new Date();
    // Reset current date time to start of day for date-only comparison
    currentDate.setHours(0, 0, 0, 0);
    
    // Ensure unfreezeRecords is an array
    if (!Array.isArray(unfreezeRecords)) {
      return false;
    }

    return unfreezeRecords.some(record => {
      const recordMatch = record.appid == appId && 
                         record.orgid == organizationId && 
                         record.sectionname === sectionName;
      
      if (recordMatch) {
        const expiryDate = new Date(record.expirydate);
        // Reset expiry date time to start of day for date-only comparison
        expiryDate.setHours(0, 0, 0, 0);
        const isNotExpired = currentDate <= expiryDate;
        return isNotExpired;
      }
      return false;
    });
  }, [unfreezeRecords, getAppId, getOrgId]);

  // Memoize the unfreeze status to prevent infinite re-renders
  const isEquipmentSectionUnfrozen = useMemo(() => {
    return isSectionUnfrozen('Equipment');
  }, [isSectionUnfrozen]);

  // Load unfreeze records
  const loadUnfreezeRecords = useCallback(async () => {
    const appId = getAppId();
    const organizationId = getOrgId();
    
    if (!appId || !organizationId) {
      setUnfreezeRecords([]); // Ensure it's always an array
      return;
    }

    try {
      const recordsApi = await UnfreezeApiService.getUnfreezeRecords(appId, organizationId);
      const records = recordsApi.data;
      // Ensure records is always an array
      if (Array.isArray(records)) {
        setUnfreezeRecords(records);
      } else {
        setUnfreezeRecords([]);
      }
    } catch (error) {
      console.error('❌ Error loading unfreeze records:', error);
      setUnfreezeRecords([]);
    }
  }, [getAppId, getOrgId]);

  // Load existing equipments when formData is ready
  useEffect(() => {
    loadEquipmentMasterData(1, '');
    if (formData.appId && formData.orgId && formData.disciplineId && formData.groupId) {
      loadExistingEquipments();
      loadUnfreezeRecords();
    }
  }, [formData.appId, formData.orgId, formData.disciplineId, formData.groupId, loadUnfreezeRecords]);

  // Clear selections when equipments change
  useEffect(() => {
    setSelectedForDelete([]);
  }, [existingEquipments]);

  // Fetch application stage for adequacy check visibility
  useEffect(() => {
    const fetchApplicationStage = async () => {
      if (!formData.appId) return;
      try {
        getApplicationStage(formData.appId);
      } catch (error) {
        console.error('Error fetching application stage:', error);
        setApplicationStage(null);
      }
    };

    fetchApplicationStage();
  }, [formData.appId]);



  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle add new equipment - now loads data in Tab 1 instead of opening modal
  const handleAddEquipment = () => {
    // When applicationStage is null, Add Equipment is at index 1
    // When applicationStage is not null, it doesn't exist (go to Existing Equipments)
    if (applicationStage === null || applicationStage === '18a' || applicationStage === "" || isEquipmentSectionUnfrozen) {
      setActiveTab(1);
      setEditingEquipment(null);
      
      // Load equipment master data for Tab 1
      loadEquipmentMasterData();
    }
  };

  // Load equipment master data
  const loadEquipmentMasterData = async (page = 1, search = '') => {
    // if (!formData.disciplineId) {
    //   showNotification('Discipline ID not found', 'error');
    //   return;
    // }

    const labTypeId = parseInt(localStorage.getItem('labTypeId') || 1);
    
    setEquipmentMasterLoading(true);
    try {
      const result = await TestingLaboratoryService.getEquipmentMaster(
        labTypeId, 
        formData.disciplineId,
        formData.groupId, 
        page, 
        pageSize, 
        search
      );
      
      if (result.success) {
        setEquipmentMasterData(result.data);
        setCurrentPage(result.pagination.currentPage);
        setTotalPages(result.pagination.totalPages);
        setTotalRecords(result.pagination.totalRecords);
        
        if (result.data.length === 0 && search) {
          showNotification('No equipment data found for the search criteria', 'info');
        }
      } else {
        showNotification(result.message || 'Failed to load equipment data', 'error');
        setEquipmentMasterData([]);
      }
    } catch (error) {
      console.error('Error loading equipment master data:', error);
      setEquipmentMasterData([]);
    } finally {
      setEquipmentMasterLoading(false);
    }
  };

  // Load all equipment data for dropdown (without pagination)
  const loadAllEquipmentData = async () => {
    if (!organizationId || !formData.disciplineId || ! formData.groupId) return;
    
    setAllEquipmentLoading(true);
    try {
      // Get all equipment by setting a very high page size
      const result = await TestingLaboratoryService.getEquipmentMaster(
        organizationId,
        formData.disciplineId,
        formData.groupId,
        1, // page
        1000, // large page size to get all records
        '' // no search filter
      );
      
      if (result?.data && Array.isArray(result.data)) {
        setAllEquipmentData(result.data);
      } else {
        setAllEquipmentData([]);
      }
    } catch (error) {
      console.error('Error loading all equipment data:', error);
      setAllEquipmentData([]);
    } finally {
      setAllEquipmentLoading(false);
    }
  };

  // Handle search
  const handleSearch = (searchValue) => {
    setSearchQuery(searchValue);
    setCurrentPage(1);
    loadEquipmentMasterData(1, searchValue);
  };

  // Handle search
  const handleSearchSubGroup = (searchValue) => {
    setSelectedSubGroup(searchValue);
    setCurrentPage(1);
    loadEquipmentMasterData(1, searchValue);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    loadEquipmentMasterData(newPage, searchQuery);
  };

  // Handle equipment selection
  const handleEquipmentSelection = (equipmentId, isSelected) => {
    if (isSelected) {
      setSelectedEquipments(prev => [...prev, equipmentId]);
      // Also store the complete equipment data
      const equipmentData = equipmentMasterData.find(equipment => equipment.eqid === equipmentId);
      if (equipmentData) {
        setSelectedEquipmentData(prev => {
          const updated = [...prev, equipmentData];
          return updated;
        });
      }
    } else {
      setSelectedEquipments(prev => prev.filter(id => id !== equipmentId));
      // Remove from selected equipment data as well
      setSelectedEquipmentData(prev => {
        const updated = prev.filter(equipment => equipment.eqid !== equipmentId);
        return updated;
      });
    }
  };

  // Handle select all
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      const currentPageIds = equipmentMasterData.map(equipment => equipment.eqid);
      setSelectedEquipments(prev => [...new Set([...prev, ...currentPageIds])]);
      // Add current page equipment data to selected equipment data (avoid duplicates)
      setSelectedEquipmentData(prev => {
        const existingIds = prev.map(equipment => equipment.eqid);
        const newEquipments = equipmentMasterData.filter(equipment => !existingIds.includes(equipment.eqid));
        const updated = [...prev, ...newEquipments];
        return updated;
      });
    } else {
      // Deselect all from current page only
      const currentPageIds = equipmentMasterData.map(equipment => equipment.eqid);
      setSelectedEquipments(prev => prev.filter(id => !currentPageIds.includes(id)));
      setSelectedEquipmentData(prev => {
        const updated = prev.filter(equipment => !currentPageIds.includes(equipment.eqid));
        return updated;
      });
    }
  };

  // Add selected equipments to existing equipments
  const handleAddSelectedEquipments = async () => {
    if (selectedEquipments.length === 0) {
      showNotification('Please select at least one equipment', 'warning');
      return;
    }

    const mainOrgid = localStorage.getItem('mainOrgId');

    // Use the stored selectedEquipmentData which contains data from all pages
    
    setLoading(true);
    try {
      const result = await TestingLaboratoryService.saveSelectedEquipments(
        selectedEquipmentData,
        formData.appId,
        mainOrgid || formData.orgId,
        parseInt(formData.disciplineId),
        parseInt(formData.groupId),
        parseInt(localStorage.getItem('labTypeId') || 1)
      );
      
      if (result.success) {
        showNotification(`Equipment(s) added successfully`, 'success');
        setSelectedEquipments([]);
        setSelectedEquipmentData([]); // Clear selected equipment data as well
        // Reload existing equipments to show the newly added ones
        loadExistingEquipments();
      } else {
        showNotification(result.message || 'Failed to save equipments', 'error');
      }
    } catch (error) {
      console.error('Error saving selected equipments:', error);
      showNotification('Error saving equipments: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load existing equipments from Form153EquipmentDetails table
  const loadExistingEquipments = async () => {
    if (!formData.appId || !formData.orgId || !formData.disciplineId || !formData.groupId) {
      return;
    }

    setExistingEquipmentsLoading(true);
    try {
      const result = await TestingLaboratoryService.getExistingEquipments(
        formData.appId,
        formData.orgId,
        formData.disciplineId,
        formData.groupId
      );
      
      if (result.success) {
        // Process equipment files for S3 signed URLs
        const processedEquipments = await s3Utils.batchProcessEquipmentFiles(
          result.data, 
          ['IQuploadFile', 'OQuploadFile', 'PQuploadFile']
        );
        setExistingEquipments(processedEquipments);
      } else {
        showNotification(result.message || 'Failed to load existing equipments', 'error');
        setExistingEquipments([]);
      }
    } catch (error) {
      console.error('Error loading existing equipments:', error);
      setExistingEquipments([]);
    } finally {
      setExistingEquipmentsLoading(false);
    }
  };

  // Handle delete equipment
  const handleDeleteEquipment = (equipmentId) => {
    setSingleDeleteId(equipmentId);
    setDeleteTarget('single');
    setDeleteConfirmOpen(true);
  };

  // Handle multiple delete
  const handleMultipleDelete = () => {
    setDeleteTarget('multiple');
    setDeleteConfirmOpen(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    try {
      setExistingEquipmentsLoading(true);
      
      if (deleteTarget === 'single') {
        const result = await TestingLaboratoryService.deleteExistingEquipment(singleDeleteId);
        if (result.success) {
          showNotification('Equipment deleted successfully', 'success');
          await loadExistingEquipments();
        } else {
          showNotification(result.message || 'Failed to delete equipment', 'error');
        }
      } else if (deleteTarget === 'multiple') {
        // Delete multiple equipments
        let successCount = 0;
        let errorCount = 0;
        
        for (const equipmentId of selectedForDelete) {
          try {
            const result = await TestingLaboratoryService.deleteExistingEquipment(equipmentId);
            if (result.success) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch (error) {
            errorCount++;
          }
        }
        
        if (successCount > 0) {
          showNotification(`${successCount} equipment(s) deleted successfully`, 'success');
          setSelectedForDelete([]);
          await loadExistingEquipments();
        }
        
        if (errorCount > 0) {
          showNotification(`${errorCount} equipment(s) could not be deleted`, 'error');
        }
      }
    } catch (error) {
      console.error('Error deleting equipment:', error);
      showNotification('Error deleting equipment: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setExistingEquipmentsLoading(false);
      setDeleteConfirmOpen(false);
      setSingleDeleteId(null);
      setDeleteTarget(null);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setSingleDeleteId(null);
    setDeleteTarget(null);
  };

  // Handle selection for delete
  const handleSelectForDelete = (equipmentId, isSelected) => {
    if (isSelected) {
      setSelectedForDelete(prev => [...prev, equipmentId]);
    } else {
      setSelectedForDelete(prev => prev.filter(id => id !== equipmentId));
    }
  };

  // Handle select all for delete
  const handleSelectAllForDelete = (isSelected) => {
    if (isSelected) {
      setSelectedForDelete(existingEquipments.map(equipment => equipment.Id));
    } else {
      setSelectedForDelete([]);
    }
  };

  // Handle edit equipment
  const handleEditEquipment = (equipment) => {
    console.log('handleEditEquipment called with equipment:', equipment);
    console.log('DateOfLastCalibration:', equipment.DateOfLastCalibration);
    console.log('CalibrationDueOn:', equipment.CalibrationDueOn);
    
    setEditingEquipmentId(equipment.Id);
    
    const formattedData = {
      range: equipment.Range || '',
      accuracy: equipment.Accuracy || '',
      yearOfMake: equipment.YearOfMake || '',
      receiptDate: formatDateForInput(equipment.ReceiptDate) || '',
      dateOfInstallation: formatDateForInput(equipment.DateOfInstallation) || '',
      limitOfDetection: equipment.LimitOfDetection || '',
      serialNo: equipment.SerialNo || '',
      makeCompany: equipment.MakeCompany || '',
      model: equipment.Model || '',
      workingStatus: equipment.Workingstatus || '',
      calibrationDueOn: formatDateForInput(equipment.CalibrationDueOn) || '',
      dateOfLastCalibration: formatDateForInput(equipment.DateOfLastCalibration) || '',
      calibratedBy: equipment.CalibratedByType || '',
      calibratedByType: equipment.CalibratedByType || '', // Database field for dropdown
      inHouseCalibration: equipment.InHouseCalibration || '', // Database field for in-house text
      externalCalibrationAgency: equipment.ExternalCalibrationAgency || '', // Database field for external agency
      uid: equipment.Uid || '',
      uploadIQ: equipment.IQuploadFile || '', // Database field name
      uploadOQ: equipment.OQuploadFile || '', // Database field name
      uploadPQ: equipment.PQuploadFile || '', // Database field name
      uploadIQOriginalName: equipment.IQuploadFile ? 'Current file' : '',
      uploadOQOriginalName: equipment.OQuploadFile ? 'Current file' : '',
      uploadPQOriginalName: equipment.PQuploadFile ? 'Current file' : ''
    };
    
    console.log('Formatted form data:', formattedData);
    setEditFormData(formattedData);
    
    // Set read-only data for display
    setEditReadOnlyData({
      equipmentName: equipment.NameOfEquipment,
      disciplineName: formData.disciplineName
    });
    
    setEditModalOpen(true);
  };

  // Handle update equipment
  const handleUpdateEquipment = async () => {
    if (!editingEquipmentId) return;

    setEditLoading(true);
    try {
      await TestingLaboratoryService.updateExistingEquipment(
        editingEquipmentId,
        editFormData
      );

      setSnackbar({
        open: true,
        message: 'Equipment updated successfully!',
        severity: 'success'
      });

      // Refresh the existing equipments list
      await loadExistingEquipments();

      // Close modal
      setEditModalOpen(false);
      setEditingEquipmentId(null);
      setEditFormData({});
      setEditReadOnlyData({});
    } catch (error) {
      console.error('Error updating equipment:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Error updating equipment',
        severity: 'error'
      });
    } finally {
      setEditLoading(false);
    }
  };

  // Handle close edit modal
  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingEquipmentId(null);
    setEditFormData({});
    setEditReadOnlyData({});
  };

  // Parse Excel/CSV file for preview
  const parseFileForPreview = async (file) => {
    return new Promise((resolve, reject) => {
      try {
        const reader = new FileReader();
        
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { 
              type: 'array',
              cellDates: false,  // Don't auto-convert to JS dates to avoid timezone issues
              raw: false,        // Convert values but preserve original format
              dateNF: 'dd-mm-yyyy' // Specify date format if needed
            });
            
            // Get the first worksheet
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            
            // Convert to JSON with header and preserve original cell values
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
              header: 1,
              raw: false, // Format values according to cell format
              defval: ''  // Default value for empty cells
            });
            
            if (jsonData.length === 0) {
              reject(new Error('File is empty'));
              return;
            }
            
            // First row as headers
            const headers = jsonData[0] || [];
            // Remaining rows as data
            const rows = jsonData.slice(1)
              .filter(row => row.some(cell => cell !== undefined && cell !== ''))
              .map((row, rowIndex) => {
                return row.map((cell, cellIndex) => {
                  if (cell === undefined || cell === null) return '';
                  
                  // Special handling for date cells to preserve exact value
                  const actualRowIndex = rowIndex + 1; // +1 because we sliced off the header
                  const cellRef = XLSX.utils.encode_cell({ r: actualRowIndex, c: cellIndex });
                  const rawCell = worksheet[cellRef];
                  
                  if (rawCell && rawCell.t === 'd') {
                    // This is a date cell - get the formatted value
                    return rawCell.w || cell.toString();
                  }
                  
                  return cell.toString();
                });
              });
            

            
            resolve({
              headers,
              data: rows,
              totalRows: rows.length
            });
          } catch (error) {
            reject(new Error('Error parsing file: ' + error.message));
          }
        };
        
        reader.onerror = () => {
          reject(new Error('Error reading file'));
        };
        
        reader.readAsArrayBuffer(file);
      } catch (error) {
        reject(error);
      }
    });
  };

  // Handle bulk file upload - now shows preview
  const handleBulkFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv' // .csv
    ];

    if (!allowedTypes.includes(file.type)) {
      setSnackbar({
        open: true,
        message: 'Please select a valid Excel (.xlsx, .xls) or CSV file',
        severity: 'error'
      });
      return;
    }

    setUploadFile(file);
    setLoading(true);
    
    try {
      setSnackbar({
        open: true,
        message: 'Parsing file for preview...',
        severity: 'info'
      });

      const parsedData = await parseFileForPreview(file);
      
      setPreviewData(parsedData.data);
      setPreviewHeaders(parsedData.headers);
      setPreviewFileName(file.name);
      // Initialize editable data immediately for default editing
      setEditedPreviewData(parsedData.data.map(row => [...row]));
      setPreviewModalOpen(true);
      
      setSnackbar({
        open: true,
        message: `File parsed successfully! Found ${parsedData.totalRows} rows of data.`,
        severity: 'success'
      });

    } catch (error) {
      console.error('Error parsing file:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Error parsing file. Please check file format.',
        severity: 'error'
      });
      setUploadFile(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle preview modal close
  const handleClosePreviewModal = () => {
    setPreviewModalOpen(false);
    setPreviewData([]);
    setPreviewHeaders([]);
    setPreviewFileName('');
    setUploadFile(null);
    setEditedPreviewData([]);
    setHasUnsavedChanges(false);
    setValidationErrors({}); // Clear validation errors
    // Reset file input
    const fileInput = document.getElementById('bulk-upload-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Handle save edited data
  const handleSaveEdit = () => {
    // Clear previous validation errors
    setValidationErrors({});
    
    const validationErrorMessages = validateEditedData();
    
    if (validationErrorMessages.length > 0) {
      // Count total errors and affected rows
      const totalErrors = validationErrorMessages.length;
      const affectedRows = new Set();
      Object.keys(validationErrors).forEach(rowIndex => {
        affectedRows.add(parseInt(rowIndex) + 1);
      });
      
      // Show detailed error summary
      let errorSummary;
      if (totalErrors === 1) {
        errorSummary = validationErrorMessages[0];
      } else if (totalErrors <= 3) {
        errorSummary = `${totalErrors} validation errors found:\n• ${validationErrorMessages.slice(0, 3).join('\n• ')}`;
      } else {
        errorSummary = `${totalErrors} validation errors found across ${affectedRows.size} rows. First 3 errors:\n• ${validationErrorMessages.slice(0, 3).join('\n• ')}\n... and ${totalErrors - 3} more errors`;
      }
      
      showNotification(
        `❌ Cannot save - please fix validation errors. ${errorSummary}`,
        'error'
      );
      return;
    }

    // All validation passed - save the data
    setPreviewData([...editedPreviewData]);
    setHasUnsavedChanges(false);
    setValidationErrors({}); // Clear any previous errors
    
    showNotification(
      '✅ Changes saved successfully! All required fields validated.',
      'success'
    );
  };

  // Handle cell value change
  const handleCellValueChange = (rowIndex, colIndex, value) => {
    // Prevent changes to non-editable columns
    if (colIndex < previewHeaders.length && isNonEditableColumn(previewHeaders[colIndex])) {
      console.warn('Attempted to edit non-editable column:', previewHeaders[colIndex]);
      return;
    }
    
    const newData = [...editedPreviewData];
    newData[rowIndex][colIndex] = value;
    setEditedPreviewData(newData);
    setHasUnsavedChanges(true);
    
    // Clear specific field error when user starts typing
    if (validationErrors[rowIndex] && validationErrors[rowIndex][colIndex]) {
      const newValidationErrors = { ...validationErrors };
      delete newValidationErrors[rowIndex][colIndex];
      
      // If no more errors for this row, remove the row entry
      if (Object.keys(newValidationErrors[rowIndex]).length === 0) {
        delete newValidationErrors[rowIndex];
      }
      
      setValidationErrors(newValidationErrors);
    }
  };

  // Validate edited data
  const validateEditedData = () => {
    const errors = [];
    const fieldErrors = {}; // Track errors by row and column for UI display
    
    editedPreviewData.forEach((row, rowIndex) => {
      previewHeaders.forEach((header, colIndex) => {
        const cellValue = row[colIndex];
        const isEmpty = !cellValue || cellValue.toString().trim() === '';
        
        // Check required fields
        if (isRequiredColumn(header) && isEmpty) {
          const errorMessage = `Row ${rowIndex + 1}: "${header}" is required`;
          errors.push(errorMessage);
          
          // Store field-specific error for UI highlighting
          if (!fieldErrors[rowIndex]) fieldErrors[rowIndex] = {};
          fieldErrors[rowIndex][colIndex] = 'This field is required';
        }
        
        // Validate date fields
        if (isDateColumn(header) && !isEmpty) {
          try {
            const dateValue = new Date(cellValue);
            if (isNaN(dateValue.getTime())) {
              const errorMessage = `Row ${rowIndex + 1}: "${header}" must be a valid date`;
              errors.push(errorMessage);
              
              if (!fieldErrors[rowIndex]) fieldErrors[rowIndex] = {};
              fieldErrors[rowIndex][colIndex] = 'Invalid date format';
            }
          } catch (error) {
            const errorMessage = `Row ${rowIndex + 1}: "${header}" must be a valid date`;
            errors.push(errorMessage);
            
            if (!fieldErrors[rowIndex]) fieldErrors[rowIndex] = {};
            fieldErrors[rowIndex][colIndex] = 'Invalid date format';
          }
        }
        
        // Validate Year of Make
        if (header && header.toLowerCase().includes('year') && !isEmpty) {
          const year = parseInt(cellValue);
          const currentYear = new Date().getFullYear();
          if (isNaN(year) || year < 1900 || year > currentYear + 1) {
            const errorMessage = `Row ${rowIndex + 1}: "${header}" must be a valid year between 1900 and ${currentYear + 1}`;
            errors.push(errorMessage);
            
            if (!fieldErrors[rowIndex]) fieldErrors[rowIndex] = {};
            fieldErrors[rowIndex][colIndex] = `Year must be between 1900-${currentYear + 1}`;
          }
        }
        
        // Validate Calibrated By field
        if (header && header.toLowerCase().includes('calibrated by') && !isEmpty) {
          const calibratedBy = cellValue.toString().toLowerCase().trim();
          if (calibratedBy !== 'in-house' && calibratedBy !== 'external') {
            const errorMessage = `Row ${rowIndex + 1}: "${header}" must be either "In-house" or "External"`;
            errors.push(errorMessage);
            
            if (!fieldErrors[rowIndex]) fieldErrors[rowIndex] = {};
            fieldErrors[rowIndex][colIndex] = 'Must be "In-house" or "External"';
          }
        }
      });
      
      // Cross-field validation for Calibrated By logic
      const calibratedByIndex = previewHeaders.findIndex(h => h && h.toLowerCase().includes('calibrated by'));
      const tracabilityIndex = previewHeaders.findIndex(h => h && h.toLowerCase().includes('traceability'));
      const agencyIndex = previewHeaders.findIndex(h => h && h.toLowerCase().includes('external') && h.toLowerCase().includes('agency'));
      
      if (calibratedByIndex !== -1 && row[calibratedByIndex]) {
        const calibratedBy = row[calibratedByIndex].toString().toLowerCase().trim();
        
        if (calibratedBy === 'in-house') {
          // If In-house, traceability should be filled and agency should be empty
          if (tracabilityIndex !== -1 && (!row[tracabilityIndex] || row[tracabilityIndex].toString().trim() === '')) {
            const errorMessage = `Row ${rowIndex + 1}: "In-house/Traceability" is required when "Calibrated By" is "In-house"`;
            errors.push(errorMessage);
            
            if (!fieldErrors[rowIndex]) fieldErrors[rowIndex] = {};
            fieldErrors[rowIndex][tracabilityIndex] = 'Required for In-house calibration';
          }
        } else if (calibratedBy === 'external') {
          // If External, agency should be filled and traceability should be empty
          if (agencyIndex !== -1 && (!row[agencyIndex] || row[agencyIndex].toString().trim() === '')) {
            const errorMessage = `Row ${rowIndex + 1}: "Name of Calibration Agency(External)" is required when "Calibrated By" is "External"`;
            errors.push(errorMessage);
            
            if (!fieldErrors[rowIndex]) fieldErrors[rowIndex] = {};
            fieldErrors[rowIndex][agencyIndex] = 'Required for External calibration';
          }
        }
      }
    });
    
    // Store field errors in state for UI display
    setValidationErrors(fieldErrors);
    
    return errors;
  };

  // Check if a column is a date column based on header name
  const isDateColumn = (header) => {
    if (!header) return false;
    const headerLower = header.toLowerCase();
    const dateKeywords = [
      'date',
      'receipt date',
      'installation date', 
      'date of installation',
      'calibration date',
      'date of last calibration',
      'calibration due',
      'due date',
      'expiry',
      'validity'
    ];
    return dateKeywords.some(keyword => headerLower.includes(keyword));
  };

  // Check if a column should be non-editable in preview
  const isNonEditableColumn = (header) => {
    if (!header) return false;
    
    const headerLower = header.toLowerCase();
    const nonEditableKeywords = [
      'equipment id',
      'discipline',
      'name of equipment'
    ];
    return nonEditableKeywords.some(keyword => headerLower.includes(keyword));
  };

  // Check if a column is required for equipment data
  const isRequiredColumn = (header) => {
    if (!header) return false;
    
    const headerLower = header.toLowerCase();
    const requiredKeywords = [
      'equipment id',
      'name of equipment',
      'make/company',
      'serial no',
      'model',
      'year of make',
      'receipt date',
      'date of installation',
      'limit of detection',
      'last calibration date',
      'calibration due on',
      'calibrated by'
    ];
    return requiredKeywords.some(keyword => headerLower.includes(keyword));
  };

  // Check if a column should use dropdown instead of text input
  const isDropdownColumn = (header) => {
    if (!header) return false;
    
    const headerLower = header.toLowerCase();
    return headerLower.includes('calibrated by');
  };

  // Format date value for display in input
  // const formatDateForInput = (value) => {
    
  //   if (!value) return '';
    
  //   // Handle JavaScript Date objects (from Excel with cellDates: true)
  //   if (value instanceof Date) {
  //     if (!isNaN(value.getTime())) {
  //       // Use local date parts to avoid timezone conversion
  //       const year = value.getFullYear();
  //       const month = String(value.getMonth() + 1).padStart(2, '0');
  //       const day = String(value.getDate()).padStart(2, '0');
  //       const formatted = `${year}-${month}-${day}`;
  //       console.log('JS Date conversion (local):', value, '->', formatted);
  //       return formatted;
  //     }
  //   }
    
  //   // If value is already in YYYY-MM-DD format, return as is
  //   if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    
  //   // Handle Excel serial dates (numbers like 44197)
  //   if (typeof value === 'number') {
  //     try {
  //       // Excel date serial number starts from 1900-01-01
  //       // Excel incorrectly treats 1900 as a leap year, so we need to adjust
  //       const excelEpoch = new Date(1899, 11, 30); // December 30, 1899
  //       const date = new Date(excelEpoch.getTime() + value * 24 * 60 * 60 * 1000);
        
  //       // Use local date parts to avoid timezone issues
  //       const year = date.getFullYear();
  //       const month = String(date.getMonth() + 1).padStart(2, '0');
  //       const day = String(date.getDate()).padStart(2, '0');
  //       const formatted = `${year}-${month}-${day}`;
        
  //       console.log('Excel serial date conversion (local):', value, '->', formatted);
        
  //       if (!isNaN(date.getTime()) && year > 1900 && year < 2100) {
  //         return formatted;
  //       }
  //     } catch (error) {
  //       console.warn('Error parsing Excel serial date:', value, error);
  //     }
  //   }
    
  //   // Handle string dates in various formats
  //   if (typeof value === 'string') {
  //     // Remove any extra whitespace
  //     const cleanValue = value.trim();
      
  //     // Handle DD-MM-YYYY format specifically (common in your Excel)
  //     const ddMMyyyyMatch = cleanValue.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  //     if (ddMMyyyyMatch) {
  //       const [, day, month, year] = ddMMyyyyMatch;
  //       const paddedDay = day.padStart(2, '0');
  //       const paddedMonth = month.padStart(2, '0');
  //       const formatted = `${year}-${paddedMonth}-${paddedDay}`;
  //       console.log('DD-MM-YYYY conversion:', cleanValue, '->', formatted);
  //       return formatted;
  //     }
      
  //     // Handle DD/MM/YYYY format
  //     const ddMMyyyySlashMatch = cleanValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  //     if (ddMMyyyySlashMatch) {
  //       const [, day, month, year] = ddMMyyyySlashMatch;
  //       const paddedDay = day.padStart(2, '0');
  //       const paddedMonth = month.padStart(2, '0');
  //       const formatted = `${year}-${paddedMonth}-${paddedDay}`;
  //       console.log('DD/MM/YYYY conversion:', cleanValue, '->', formatted);
  //       return formatted;
  //     }
      
  //     // Handle MM-DD-YYYY format (US format)
  //     const mmDDyyyyMatch = cleanValue.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  //     if (mmDDyyyyMatch) {
  //       const [, month, day, year] = mmDDyyyyMatch;
  //       // Check if this makes sense as MM-DD (US format)
  //       if (parseInt(month) <= 12 && parseInt(day) <= 31) {
  //         const paddedDay = day.padStart(2, '0');
  //         const paddedMonth = month.padStart(2, '0');
  //         const formatted = `${year}-${paddedMonth}-${paddedDay}`;
  //         console.log('MM-DD-YYYY conversion:', cleanValue, '->', formatted);
  //         return formatted;
  //       }
  //     }
      
  //     // Try general date parsing as last resort (but preserve local date)
  //     try {
  //       const date = new Date(cleanValue);
  //       if (!isNaN(date.getTime()) && date.getFullYear() > 1900 && date.getFullYear() < 2100) {
  //         // Use local date parts to avoid timezone conversion
  //         const year = date.getFullYear();
  //         const month = String(date.getMonth() + 1).padStart(2, '0');
  //         const day = String(date.getDate()).padStart(2, '0');
  //         const formatted = `${year}-${month}-${day}`;
  //         console.log('General date parsing (local):', cleanValue, '->', formatted);
  //         return formatted;
  //       }
  //     } catch (error) {
  //       console.warn('Error parsing date string:', cleanValue);
  //     }
  //   }
    
  //   // If all else fails, return the original value
  //   return value.toString();
  // };

  // Handle confirm upload from preview
  const handleConfirmUpload = () => {
    setPreviewModalOpen(false);
    // Call the actual bulk upload function
    handleBulkUpload();
  };

  // Handle IQ file upload to S3 only (don't save to database yet)
  const handleIQUpload = async (file, fieldName = 'uploadIQ') => {
    try {
      setEditLoading(true);
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('orgId', organizationId);
      formData.append('section', 'equipment/iq');
      formData.append('fieldName', fieldName);

      // Upload to S3 via the file upload API
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/files/upload`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        // Update state with file info
        setEditFormData(prev => ({ 
          ...prev, 
          uploadIQ: result.data.key,
          uploadIQOriginalName: result.data.originalName 
        }));
        
        setSnackbar({
          open: true,
          message: 'IQ file uploaded successfully! Click Update to save changes.',
          severity: 'success'
        });
        
        return result;
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to upload IQ file',
        severity: 'error'
      });
      throw error;
    } finally {
      setEditLoading(false);
    }
  };

  // Handle OQ file upload to S3 only (don't save to database yet)
  const handleOQUpload = async (file, fieldName = 'uploadOQ') => {
    try {
      setEditLoading(true);
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('orgId', organizationId);
      formData.append('section', 'equipment/oq');
      formData.append('fieldName', fieldName);

      // Upload to S3 via the file upload API
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/files/upload`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        // Update state with file info
        setEditFormData(prev => ({ 
          ...prev, 
          uploadOQ: result.data.key,
          uploadOQOriginalName: result.data.originalName 
        }));
        
        setSnackbar({
          open: true,
          message: 'OQ file uploaded successfully! Click Update to save changes.',
          severity: 'success'
        });
        
        return result;
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to upload OQ file',
        severity: 'error'
      });
      throw error;
    } finally {
      setEditLoading(false);
    }
  };

  // Handle PQ file upload to S3 only (don't save to database yet)
  const handlePQUpload = async (file, fieldName = 'uploadPQ') => {
    try {
      setEditLoading(true);
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('orgId', organizationId);
      formData.append('section', 'equipment/pq');
      formData.append('fieldName', fieldName);

      // Upload to S3 via the file upload API
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/files/upload`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        // Update state with file info
        setEditFormData(prev => ({ 
          ...prev, 
          uploadPQ: result.data.key,
          uploadPQOriginalName: result.data.originalName 
        }));
        
        setSnackbar({
          open: true,
          message: 'PQ file uploaded successfully! Click Update to save changes.',
          severity: 'success'
        });
        
        return result;
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to upload PQ file',
        severity: 'error'
      });
      throw error;
    } finally {
      setEditLoading(false);
    }
  };

  // Handle file view using S3 utils with enhanced functionality
  const handleFileView = async (fileKey) => {
    const success = await s3Utils.handleFileViewWithS3(fileKey, {
      showError: (msg) => setSnackbar({
        open: true,
        message: msg,
        severity: 'error'
      }),
      showLoading: () => {}, // Could add loading state here if needed
      hideLoading: () => {},
      expiresIn: 172800 // 48 hours
    });
    
    if (!success) {
      console.log('File view failed for key:', fileKey);
    }
  };
  
  // Utility function to validate and format dates for SQL Server
  const validateAndFormatDate = (dateValue, fieldName, rowNumber) => {
    if (!dateValue || dateValue.toString().trim() === '') {
      return null;
    }
    
    try {
      let dateStr = dateValue.toString().trim();
      let convertedDate = null;
      
      // Handle DD/MM/YYYY format
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const [day, month, year] = parts.map(p => parseInt(p.trim()));
          
          if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
            // Create date object to validate the actual date
            const testDate = new Date(year, month - 1, day);
            if (testDate.getDate() === day && testDate.getMonth() === month - 1 && testDate.getFullYear() === year) {
              convertedDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
            }
          }
        }
      }
      // Handle DD-MM-YYYY format
      else if (dateStr.includes('-')) {
        const parts = dateStr.split('-');
        if (parts.length === 3) {
          // Check if it's already in YYYY-MM-DD format
          if (parts[0].length === 4) {
            const [year, month, day] = parts.map(p => parseInt(p.trim()));
            if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
              const testDate = new Date(year, month - 1, day);
              if (testDate.getDate() === day && testDate.getMonth() === month - 1 && testDate.getFullYear() === year) {
                convertedDate = dateStr; // Already in correct format
              }
            }
          } else {
            // Assume DD-MM-YYYY format
            const [day, month, year] = parts.map(p => parseInt(p.trim()));
            if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
              const testDate = new Date(year, month - 1, day);
              if (testDate.getDate() === day && testDate.getMonth() === month - 1 && testDate.getFullYear() === year) {
                convertedDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
              }
            }
          }
        }
      }
      // Handle Excel date numbers
      else if (!isNaN(dateStr) && parseFloat(dateStr) > 0) {
        const excelDateNum = parseFloat(dateStr);
        // Excel epoch starts from January 1, 1900 (with leap year bug consideration)
        // Use local time to avoid timezone conversion issues
        const excelEpoch = new Date(1899, 11, 30); // December 30, 1899
        const jsDate = new Date(excelEpoch.getTime() + (excelDateNum * 24 * 60 * 60 * 1000));
        
        if (!isNaN(jsDate.getTime()) && jsDate.getFullYear() >= 1900 && jsDate.getFullYear() <= 2100) {
          // Use local date format to avoid timezone issues
          const year = jsDate.getFullYear();
          const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
          const day = jsDate.getDate().toString().padStart(2, '0');
          convertedDate = `${year}-${month}-${day}`;
        }
      }
      
      // Final validation using local date to avoid timezone issues
      if (convertedDate) {
        // Parse the date components to avoid timezone conversion
        const [year, month, day] = convertedDate.split('-').map(num => parseInt(num));
        const validationDate = new Date(year, month - 1, day); // Local date constructor
        
        if (!isNaN(validationDate.getTime()) && 
            validationDate.getFullYear() === year && 
            validationDate.getMonth() === month - 1 && 
            validationDate.getDate() === day) {
          return convertedDate;
        }
      }
      
      console.warn(`Could not convert date for row ${rowNumber}, field ${fieldName}: "${dateValue}"`);
      return null;
      
    } catch (error) {
      console.error(`Date validation error for row ${rowNumber}, field ${fieldName}:`, error);
      return null;
    }
  };

  // Handle bulk upload with edited preview data
  const handleBulkUpload = async () => {
    // Check if we have edited preview data (from the preview modal)
    if (editedPreviewData.length === 0) {
      showNotification('No preview data available. Please parse a file first.', 'warning');
      return;
    }

    setLoading(true);
    
    try {
      showNotification('Processing edited data...', 'info');

      // Use the edited preview data instead of re-parsing the file
      const headers = previewHeaders;
      const dataRows = editedPreviewData;
      
      console.log('Using edited preview data:');
      console.log('Headers:', headers);
      console.log('Data rows count:', dataRows.length);

      // Define expected column mapping (case-insensitive)
      const columnMapping = {
        'equipment id': 'equipmentId',
        'equipmentid': 'equipmentId',
        'uid of equipment': 'uid',
        'uid': 'uid',
        'name of equipment': 'equipmentName',
        'equipment name': 'equipmentName',
        'equipmentname': 'equipmentName',
        'make/company': 'manufacturerName',
        'nameofmanufacturer': 'manufacturerName',
        'manufacturer': 'manufacturerName',
        'serial no': 'serialNo',
        'serialno': 'serialNo',
        'model': 'model',
        'year of make': 'yearOfMake',
        'yearofmake': 'yearOfMake',
        'receipt date': 'receiptDate',
        'receiptdate': 'receiptDate',
        'date of installation': 'dateOfInstallation',
        'dateofinstallation': 'dateOfInstallation',
        'limit of detection': 'limitOfDetection',
        'limitofdetection': 'limitOfDetection',
        'last calibration date': 'dateOfLastCalibration',
        'dateofLastcalibration': 'dateOfLastCalibration',
        'calibration due on': 'calibrationDueOn',
        'calibrationdueon': 'calibrationDueOn',
        'calibrated by': 'calibratedBy',
        'calibratedby': 'calibratedBy',
        'in-house/traceability': 'traceability',
        'traceability': 'traceability',
        'Name of Calibration Agency(External)': 'calibrationAgencyName',
        'calibrationagencyname': 'calibrationAgencyName'
      };

      // Create column index mapping
      const columnIndexes = {};
      headers.forEach((header, index) => {
        if (header && typeof header === 'string') {
          const normalizedHeader = header.toLowerCase().trim();
          const mappedField = columnMapping[normalizedHeader];
          if (mappedField) {
            columnIndexes[mappedField] = index;
            console.log(`Column mapping: "${header}" -> "${mappedField}" at index ${index}`);
          } else {
            console.warn(`No mapping found for header: "${header}" (normalized: "${normalizedHeader}")`);
          }
        }
      });

      // Debug: Show all found column mappings
      console.log('All column mappings:', columnIndexes);

      // Validate required columns
      const requiredFields = ['equipmentId', 'equipmentName'];
      const missingFields = requiredFields.filter(field => columnIndexes[field] === undefined);
      
      if (missingFields.length > 0) {
        showNotification(`Missing required columns: ${missingFields.join(', ')}. Please check your Excel file.`, 'error');
        return;
      }

      showNotification('Validating data...', 'info');

      // Process each data row
      const equipmentData = [];
      const errors = [];
      
      for (let i = 0; i < dataRows.length; i++) {
        const row = dataRows[i];
        const rowNumber = i + 2; // +2 because Excel is 1-indexed and we skipped header
        
        // Skip empty rows
        if (!row || row.every(cell => !cell || cell.toString().trim() === '')) {
          continue;
        }

        try {
          // Extract data from edited preview row using column indexes
          const equipment = {
            equipmentId: row[columnIndexes.equipmentId]?.toString().trim() || '',
            equipmentName: row[columnIndexes.equipmentName]?.toString().trim() || '',
            manufacturerName: row[columnIndexes.manufacturerName]?.toString().trim() || '',
            uid: row[columnIndexes.uid]?.toString().trim() || '',
            serialNo: row[columnIndexes.serialNo]?.toString().trim() || '',
            model: row[columnIndexes.model]?.toString().trim() || '',
            yearOfMake: row[columnIndexes.yearOfMake] ? parseInt(row[columnIndexes.yearOfMake]) : null,
            receiptDate: row[columnIndexes.receiptDate]?.toString().trim() || '',
            dateOfInstallation: row[columnIndexes.dateOfInstallation]?.toString().trim() || '',
            limitOfDetection: row[columnIndexes.limitOfDetection]?.toString().trim() || '',
            dateOfLastCalibration: row[columnIndexes.dateOfLastCalibration]?.toString().trim() || '',
            calibrationDueOn: row[columnIndexes.calibrationDueOn]?.toString().trim() || '',
            calibratedBy: row[columnIndexes.calibratedBy]?.toString().trim() || '',
            traceability: row[columnIndexes.traceability]?.toString().trim() || '',
            calibrationAgencyName: row[columnIndexes.calibrationAgencyName]?.toString().trim() || ''
          };

          // Debug logging for edited data extraction
          console.log(`Row ${rowNumber} - Equipment ${equipment.equipmentId} (from edited data):`, {
            originalUID: equipment.uid,
            editedUID: row[columnIndexes.uid], // Show the actual edited value
            manufacturerName: equipment.manufacturerName,
            limitOfDetection: equipment.limitOfDetection,
            calibratedBy: equipment.calibratedBy,
            rowData: row // Show the complete row data
          });

          // Validate required fields
          if (!equipment.equipmentId || !equipment.equipmentName) {
            errors.push(`Row ${rowNumber}: Equipment ID and Equipment Name are required`);
            continue;
          }

          // Convert date formats using the utility function
          const dateFields = ['receiptDate', 'dateOfInstallation', 'dateOfLastCalibration', 'calibrationDueOn'];
          dateFields.forEach(field => {
            equipment[field] = validateAndFormatDate(equipment[field], field, rowNumber);
          });

          equipmentData.push(equipment);

        } catch (rowError) {
          console.error(`Error processing row ${rowNumber}:`, rowError);
          errors.push(`Row ${rowNumber}: ${rowError.message}`);
        }
      }

      if (errors.length > 0) {
        showNotification(`Found ${errors.length} error(s) in file. Please check console for details.`, 'warning');
        console.error('Bulk upload errors:', errors);
        // Continue with valid rows
      }

      if (equipmentData.length === 0) {
        showNotification('No valid equipment data found in file', 'error');
        return;
      }

      showNotification(`Processing ${equipmentData.length} equipment records...`, 'info');

      // Process equipment data - update existing only (skip new entries)
      let updatedCount = 0;
      let skippedCount = 0;
      let errorCount = 0;
      const skippedEquipments = [];

      for (const equipment of equipmentData) {
        try {
          // Check if equipment exists by Equipment ID
          const existingEquipment = existingEquipments.find(
            existing => existing.Equipmentid === equipment.equipmentId
          );

          console.log(`Looking for equipment with ID: "${equipment.equipmentId}"`);
          console.log(`Found existing equipment:`, existingEquipment ? `ID: ${existingEquipment.Id}, Name: ${existingEquipment.EquipmentName}` : 'NOT FOUND');
          console.log(`Total existing equipments: ${existingEquipments.length}`);
          console.log(`Sample existing equipment IDs:`, existingEquipments.slice(0, 3).map(eq => `"${eq.Equipmentid}"`));

          if (existingEquipment) {
            // Update existing equipment only
            // Process calibration fields properly
            let calibratedByType = '';
            let inHouseCalibration = '';
            let externalCalibrationAgency = '';
            
            const calibratedByValue = equipment.calibratedBy?.toLowerCase()?.trim();
            
            if (calibratedByValue === 'in-house' || calibratedByValue === 'inhouse') {
              calibratedByType = 'In-house';
              inHouseCalibration = equipment.traceability || '';
            } else if (calibratedByValue === 'external') {
              calibratedByType = 'External'; 
              externalCalibrationAgency = equipment.calibrationAgencyName || '';
            } else if (calibratedByValue) {
              // If there's a value but not recognized, use it as-is
              calibratedByType = equipment.calibratedBy;
              // Check if we have traceability or agency data to determine type
              if (equipment.traceability && !equipment.calibrationAgencyName) {
                inHouseCalibration = equipment.traceability;
              } else if (equipment.calibrationAgencyName && !equipment.traceability) {
                externalCalibrationAgency = equipment.calibrationAgencyName;
              }
            }

            // Debug logging for calibration fields
            console.log(`Equipment ${equipment.equipmentId} - Calibration Data Processing:`, {
              originalData: {
                calibratedBy: equipment.calibratedBy,
                traceability: equipment.traceability,
                calibrationAgencyName: equipment.calibrationAgencyName
              },
              processedData: {
                calibratedByType: calibratedByType,
                inHouseCalibration: inHouseCalibration,
                externalCalibrationAgency: externalCalibrationAgency
              }
            });

            // Debug formData before creating payload
            console.log('📝 Equipment Save - FormData Check:', {
              appId: formData.appId,
              orgId: formData.orgId,
              disciplineId: formData.disciplineId,
              disciplineName: formData.disciplineName
            });

            // Validate required IDs
            if (!formData.disciplineId) {
              console.error('❌ Equipment Save - Missing disciplineId!');
              showNotification('Error: Discipline ID is missing. Please navigate back from discipline section.', 'error');
              return;
            }

            const equipmentPayload = {
              // Basic IDs - same as individual edit
              appId: parseInt(formData.appId),
              orgId: parseInt(formData.orgId),
              disciplineId: parseInt(formData.disciplineId),
              groupId: formData.groupId, 
              
              // Equipment basic info - matching individual edit field names
              equipmentId: equipment.equipmentId,
              equipmentName: equipment.equipmentName,
              makeCompany: equipment.manufacturerName || '',  // Correct field name
              uid: equipment.uid || '',
              serialNo: equipment.serialNo || '',
              model: equipment.model || '',
              yearOfMake: equipment.yearOfMake || null,
              
              // Dates - ensure consistent format
              receiptDate: equipment.receiptDate || null,
              dateOfInstallation: equipment.dateOfInstallation || null,
              dateOfLastCalibration: equipment.dateOfLastCalibration || null,
              calibrationDueOn: equipment.calibrationDueOn || null,
              
              // Equipment specific fields
              limitOfDetection: equipment.limitOfDetection || '',
              range: '', // Default empty unless provided in Excel
              accuracy: '', // Default empty unless provided in Excel
              workingStatus: '', // Default empty unless provided in Excel
              
              // Calibration fields - matching individual edit structure
              calibratedBy: calibratedByType, // Main calibration type
              calibratedByType: calibratedByType, // Database field for dropdown
              inHouseCalibration: inHouseCalibration, // Database field for in-house text
              externalCalibrationAgency: externalCalibrationAgency, // Database field for external agency
              
              // Keep existing file uploads - matching individual edit field names
              uploadIQ: existingEquipment?.IQuploadFile || '', // Database field name
              uploadOQ: existingEquipment?.OQuploadFile || '', // Database field name
              uploadPQ: existingEquipment?.PQuploadFile || '', // Database field name
            };

            console.log(`Updating equipment ${equipment.equipmentId} with payload:`, equipmentPayload);
            console.log(`Using equipment ID from existing data: ${existingEquipment.Id}`);
            
            const updateResult = await TestingLaboratoryService.updateExistingEquipment(existingEquipment.Id, equipmentPayload);
            console.log(`Update result for equipment ${equipment.equipmentId}:`, updateResult);
            updatedCount++;
          } else {
            // Skip new equipment (not in existing data)
            console.log(`Skipping new equipment ${equipment.equipmentId} - not in existing data`);
            skippedCount++;
            skippedEquipments.push({
              equipmentId: equipment.equipmentId,
              equipmentName: equipment.equipmentName
            });
          }

        } catch (equipmentError) {
          console.error(`Error processing equipment ${equipment.equipmentId}:`, equipmentError);
          
          // Log the specific error details
          if (equipmentError.response?.data) {
            console.error('API Error Response:', equipmentError.response.data);
          }
          
          errorCount++;
        }
      }

      // Show results with detailed information
      const resultMessages = [];
      if (updatedCount > 0) resultMessages.push(`${updatedCount} equipment(s) updated`);
      if (skippedCount > 0) resultMessages.push(`${skippedCount} new equipment(s) skipped`);
      if (errorCount > 0) resultMessages.push(`${errorCount} error(s) occurred`);

      // Create detailed notification message
      let notificationMessage = `Bulk upload completed! ${resultMessages.join(', ')}.`;
      
      // Add detailed information about skipped equipment
      if (skippedCount > 0) {
        const skippedList = skippedEquipments.map(eq => `"${eq.equipmentId} - ${eq.equipmentName}"`).join(', ');
        console.log(`Skipped Equipment Details: ${skippedList}`);
        
        // Show detailed skipped information
        const skippedMessage = `\n\nSkipped Equipment (not in existing data):\n${skippedEquipments.map((eq, index) => 
          `${index + 1}. Equipment ID: ${eq.equipmentId} - ${eq.equipmentName}`
        ).join('\n')}`;
        
        // For console logging
        console.warn('The following equipment entries were skipped because they are not in your existing data:', skippedEquipments);
        
        // Add additional notification for skipped items
        setTimeout(() => {
          showNotification(
            `${skippedCount} equipment entries were skipped because they are not in your existing data. Check console for details.`,
            'warning'
          );
        }, 2000);
      }

      showNotification(
        notificationMessage,
        errorCount > 0 ? 'warning' : (skippedCount > 0 ? 'info' : 'success')
      );

      // Refresh the existing equipments list
      await loadExistingEquipments();

      // Clear uploaded file
      setUploadFile(null);
      const fileInput = document.getElementById('bulk-upload-input');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      console.error('Bulk upload error:', error);
      showNotification('Error processing file: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Show notification
  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  // Close notification
  const closeNotification = () => {
    setNotification({ open: false, message: '', severity: 'success' });
  };


  // Load equipment dropdown data using new API
  const loadEquipmentDropdownData = async () => {
    if (!formData.disciplineId || !formData.groupId) {
      showNotification('Discipline & Group ID is required', 'warning');
      return;
    }
    
    setEquipmentDropdownLoading(true);
    try {
      // Lab type ID for medical is typically 3, but you can get it from context if needed
      const labTypeId = 1; // Medical lab type
      
      const result = await TestingLaboratoryService.getEquipmentMasterForDropdown(labTypeId, formData.disciplineId, formData.groupId);
      
      if (result?.data && Array.isArray(result.data)) {
        setEquipmentDropdownData(result.data);
        showNotification(`Loaded ${result.count || result.data.length} equipment(s) for dropdown`, 'success');
      } else {
        setEquipmentDropdownData([]);
        showNotification('No equipment data found', 'info');
      }
    } catch (error) {
      console.error('Error loading equipment dropdown data:', error);
      setEquipmentDropdownData([]);
    } finally {
      setEquipmentDropdownLoading(false);
    }
  };

  // Add Other Equipment Modal Handlers
  const handleAddOtherEquipment = () => {
    setAddOtherModalOpen(true);
    // No longer need to load equipment dropdown data since we're using text field
  };

  const handleCloseOtherModal = () => {
    setAddOtherModalOpen(false);
    // Reset form
    setOtherEquipmentForm({
      equipmentId: '',
      equipmentName: '',
      manufacturerName: '',
      uid: '',
      serialNo: '',
      model: '',
      yearOfMake: new Date().getFullYear(),
      receiptDate: '',
      dateOfInstallation: '',
      limitOfDetection: '',
      dateOfLastCalibration: '',
      calibrationDueOn: '',
      calibratedBy: 'inhouse',
      traceability: '',
      calibrationAgencyName: ''
    });
    setOtherEquipmentFiles({
      iqFile: null,
      oqFile: null,
      pqFile: null
    });
  };

  const handleOtherFormChange = (field, value) => {
    // Validate calibration due date to ensure it's not in the past
    if (field === 'calibrationDueOn' && value) {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to compare only dates
      
      if (selectedDate < today) {
        showNotification('Calibration due date cannot be in the past. Please select today or a future date.', 'warning');
        return; // Don't update the state with invalid date
      }
    }
    
    // Validate date of last calibration to ensure it's not in the future
    if (field === 'dateOfLastCalibration' && value) {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(23, 59, 59, 999); // Allow today's date
      
      if (selectedDate > today) {
        showNotification('Date of last calibration cannot be in the future. Please select today or a past date.', 'warning');
        return; // Don't update the state with invalid date
      }
    }
    
    setOtherEquipmentForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Download QR codes as PDF in tabular format
  const downloadQRCodesPDF = async () => {
    if (existingEquipments.length === 0) {
      showNotification('No equipment data available to generate QR codes', 'warning');
      return;
    }

    setPdfDownloading(true);
    try {
      showNotification('Generating QR codes PDF...', 'info');

      // Create PDF document
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // PDF styling
      const margin = 15;
      const qrSize = 25; // QR code size in mm
      const cellWidth = (pageWidth - 2 * margin) / 5; // 5 columns
      const cellHeight = qrSize + 25; // QR + text space
      const cols = 5;
      let currentRow = 0;
      let currentCol = 0;
      let pageNumber = 1;

      // Calculate how many rows fit per page
      const headerSpace = 40; // Space for title and date
      const availableHeight = pageHeight - (2 * margin) - headerSpace;
      const rowsPerPage = Math.floor(availableHeight / cellHeight);
      const itemsPerPage = rowsPerPage * cols;
    
      // Add header
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Equipment QR Codes', pageWidth / 2, margin, { align: 'center' });
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Generated on: ${new Date().toLocaleString()}`, pageWidth / 2, margin + 7, { align: 'center' });
      
      // Starting position for QR codes
      let startY = margin + 20;
      let processedCount = 0;
      let skippedCount = 0;
      
      for (let i = 0; i < existingEquipments.length; i++) {
        const equipment = existingEquipments[i];
        // Use the same equipment ID logic as QR URL generation
        const equipmentId = equipment.EquipmentId || equipment.equipmentId || equipment.Id || equipment.Equipmentid;
        const equipmentUid = equipment.Equipmentid
        const equipmentName = equipment.NameOfEquipment || equipment.equipmentName || 'Unknown Equipment';
        const qrUrl = qrCodeUrls[equipmentId];

        if (!equipmentId) {
          console.warn(`No equipment ID found for equipment at index ${i}:`, equipment);
          skippedCount++;
          continue;
        }

        if (!qrUrl) {
          console.warn(`No QR URL found for equipment ID: ${equipmentId}`);
          console.log('Available QR URLs:', Object.keys(qrCodeUrls));
          console.log('Equipment object:', equipment);
          skippedCount++;
          continue;
        }

        // Check if we need a new page (check if current row would exceed page height)
        let currentY = startY + (currentRow * cellHeight);
        if (currentY + cellHeight > pageHeight - margin) {
          console.log(`📄 Page break at item ${i + 1}: currentY=${currentY}, cellHeight=${cellHeight}, pageHeight=${pageHeight}, margin=${margin}`);
          pdf.addPage();
          pageNumber++;
          
          // Add header to new page
          pdf.setFontSize(16);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Equipment QR Codes', pageWidth / 2, margin, { align: 'center' });
          
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          pdf.text(`Generated on: ${new Date().toLocaleString()}`, pageWidth / 2, margin + 7, { align: 'center' });
          
          startY = margin + 20; // Reset to initial Y position with header space
          currentRow = 0;
          currentCol = 0; // Reset column too when starting new page
          currentY = startY + (currentRow * cellHeight); // Recalculate Y for new page
          console.log(`📄 New page ${pageNumber} started, new startY=${startY}`);
        }

        // Calculate position
        const x = margin + (currentCol * cellWidth);
        const y = currentY;

        try {
          // Generate QR code as base64 image
          const qrCodeDataURL = await QRCodeLib.toDataURL(qrUrl, {
            width: 100,
            margin: 2,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            }
          });

          // Add QR code to PDF
          pdf.addImage(qrCodeDataURL, 'PNG', x + (cellWidth - qrSize) / 2, y, qrSize, qrSize);

          // Add equipment ID below QR code
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`ID: ${equipmentUid}`, x + cellWidth / 2, y + qrSize + 7, { align: 'center', maxWidth: cellWidth - 4 });

          // Add equipment name (truncated if too long)
          pdf.setFont('helvetica', 'normal');
          const truncatedName = equipmentName.length > 25 ? equipmentName.substring(0, 22) + '...' : equipmentName;
          pdf.text(truncatedName, x + cellWidth / 2, y + qrSize + 14, { align: 'center', maxWidth: cellWidth - 4 });

          // Add border around cell
          pdf.setDrawColor(200, 200, 200);
          pdf.rect(x, y, cellWidth, cellHeight);

          processedCount++;

        } catch (error) {
          console.error(`Error generating QR code for equipment ${equipmentId}:`, error);
          
          // Add error placeholder
          pdf.setFontSize(8);
          pdf.text('QR Error', x + cellWidth / 2, y + qrSize / 2, { align: 'center' });
          pdf.text(`ID: ${equipmentId}`, x + cellWidth / 2, y + qrSize + 7, { align: 'center' });
          
          processedCount++; // Count error placeholders too
        }

        // Move to next position
        currentCol++;
        if (currentCol >= cols) {
          currentCol = 0;
          currentRow++;
        }
      }

      console.log('📊 QR Code Processing Summary:');
      console.log('Processed successfully:', processedCount);
      console.log('Skipped due to missing data:', skippedCount);
      console.log('Total equipments:', existingEquipments.length);

      // Add page numbers
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
      }

      // Save PDF
      const fileName = `Equipment_QR_Codes_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

      const successMessage = processedCount === existingEquipments.length 
        ? `QR codes PDF downloaded successfully! (${processedCount} equipments)`
        : `QR codes PDF downloaded! (${processedCount} processed, ${skippedCount} skipped out of ${existingEquipments.length} total equipments)`;
      
      showNotification(successMessage, processedCount === existingEquipments.length ? 'success' : 'warning');

    } catch (error) {
      console.error('Error generating QR codes PDF:', error);
      showNotification('Error generating QR codes PDF: ' + error.message, 'error');
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleOtherFileUpload = async (file, fileType) => {
    if (!file) return;
    
    console.log('Starting file upload:', { fileName: file.name, fileType, organizationId, disciplineId: formData.disciplineId });
    
    try {
      setOtherEquipmentLoading(true);
      
      // Show uploading notification
      showNotification(`Uploading ${fileType.toUpperCase()} file...`, 'info');
      
      // Create folder path for organization and app
      const folderPath = `applications/${organizationId}/equipment/${formData.disciplineId}`;
      console.log('Upload folder path:', folderPath);
      
      // Upload file to S3 using S3Service
      const response = await S3Service.uploadFile(
        file, 
        folderPath, 
        `${fileType}_${Date.now()}_${file.name}`,
        (progress) => {
          console.log(`Upload progress: ${progress}%`);
        }
      );
      
      console.log('Upload response:', response);
      
      if (response?.success && response?.data?.key) {
        // Store file info in state with file details
        setOtherEquipmentFiles(prev => ({
          ...prev,
          [fileType]: {
            fileKey: response.data.key,
            fileName: file.name,
            fileSize: file.size,
            uploadedAt: new Date().toISOString(),
            url: response.data.url || response.data.Location
          }
        }));
        
        console.log('File uploaded successfully:', response.data.key);
        showNotification(`${fileType.toUpperCase()} file uploaded successfully! File will be saved when you click "Add Equipment".`, 'success');
      } else {
        console.error('Upload failed - no file key:', response);
        throw new Error(response?.error || 'No file key received from server');
      }
    } catch (error) {
      console.error(`Error uploading ${fileType} file:`, error);
      showNotification(`Error uploading ${fileType} file: ${error.message}`, 'error');
      
      // Clear the file from state if upload failed
      setOtherEquipmentFiles(prev => ({
        ...prev,
        [fileType]: null
      }));
    } finally {
      setOtherEquipmentLoading(false);
    }
  };

  const handleSubmitOtherEquipment = async () => {
    try {
      setOtherEquipmentLoading(true);

      // Get values from encrypted query parameters and URL params
      const currentAppId = getAppId();
      const currentOrgId = getOrgId();
      console.log('🔍 EquipmentSection - handleOtherFileUpload using orgId:', currentOrgId, 'appId:', currentAppId);
      // Use decrypted disciplineId from formData instead of raw encrypted one from URL
      const disciplineId = formData.disciplineId;
      const groupId = formData.groupId;

      // Validate required fields
      const requiredFields = ['equipmentName', 'manufacturerName', 'uid', 'model', 'yearOfMake', 
                             'receiptDate', 'dateOfInstallation', 'dateOfLastCalibration', 'calibrationDueOn'];
      const missingFields = requiredFields.filter(field => !otherEquipmentForm[field]);
      
      if (missingFields.length > 0) {
        showNotification(`Please fill all required fields: ${missingFields.join(', ')}`, 'warning');
        return;
      }

      if (!currentAppId || !currentOrgId || !disciplineId) {
        showNotification('Missing application parameters. Please refresh and try again.', 'error');
        return;
      }

      if (!otherEquipmentFiles.pqFile) {
        showNotification('PQ file is required', 'warning');
        return;
      }

      // Generate a unique equipmentId for custom equipment in format "other-001"
      const customEquipmentId = `other-${String(Date.now()).slice(-3).padStart(3, '0')}`;
      
      // Prepare equipment data with file keys
      const equipmentData = {
        appId: parseInt(currentAppId),
        orgId: parseInt(currentOrgId),
        disciplineId: parseInt(disciplineId),
        groupId: groupId,
        equipmentId: customEquipmentId, // Generate unique ID for custom equipment
        equipmentName: otherEquipmentForm.equipmentName,
        manufacturerName: otherEquipmentForm.manufacturerName,
        uid: otherEquipmentForm.uid,
        serialNo: otherEquipmentForm.serialNo,
        model: otherEquipmentForm.model,
        yearOfMake: parseInt(otherEquipmentForm.yearOfMake),
        receiptDate: otherEquipmentForm.receiptDate,
        dateOfInstallation: otherEquipmentForm.dateOfInstallation,
        limitOfDetection: otherEquipmentForm.limitOfDetection,
        dateOfLastCalibration: otherEquipmentForm.dateOfLastCalibration,
        calibrationDueOn: otherEquipmentForm.calibrationDueOn,
        calibratedBy: otherEquipmentForm.calibratedBy,
        traceability: otherEquipmentForm.traceability,
        calibrationAgencyName: otherEquipmentForm.calibrationAgencyName,
        iqUploadFile: otherEquipmentFiles.iqFile?.fileKey || null,
        oqUploadFile: otherEquipmentFiles.oqFile?.fileKey || null,
        pqUploadFile: otherEquipmentFiles.pqFile?.fileKey || null
      };

      // Save equipment to database
      await TestingLaboratoryService.saveEquipment(equipmentData);
      
      showNotification('Equipment added successfully!', 'success');
      handleCloseOtherModal();
      
      // Refresh existing equipments list
      if (typeof loadExistingEquipments === 'function') {
        loadExistingEquipments();
      }
    } catch (error) {
      console.error('Error adding equipment:', error);
      showNotification('Error adding equipment', 'error');
    } finally {
      setOtherEquipmentLoading(false);
    }
  };

  // Bulk Adequacy Check Handlers
  const handleOpenBulkAdequacyModal = () => {
    if (selectedAdequacyEquipments.length === 0) {
      showNotification('Please select at least one equipment for adequacy check', 'warning');
      return;
    }
    setBulkAdequacyModalOpen(true);
    fetchBulkScrutinyRemarks();
  };

  const fetchBulkScrutinyRemarks = async () => {
    setBulkLoadingRemarks(true);
    try {
      const responseApi = await getScrutinyRemarks({ 
        sectionName: 'Equipment', 
        type: localStorage.getItem('userTypeId') === '4' ? 'Document Review' : 'Assessment', 
      });
      const response = responseApi.data;
      if (response.success && response.data) {
        setBulkScrutinyRemarks(response.data);
        console.log('Bulk Equipment: Loaded scrutiny remarks:', response.data.length);
      } else {
        setBulkScrutinyRemarks([]);
      }
    } catch (error) {
      console.error('Error fetching bulk scrutiny remarks:', error);
      setBulkScrutinyRemarks([]);
    } finally {
      setBulkLoadingRemarks(false);
    }
  };

  const handleCloseBulkAdequacyModal = () => {
    setBulkAdequacyModalOpen(false);
    setBulkAdequacyStatus('');
    setBulkAdequacyRemarks('');
    setBulkScrutinyRemarks([]);
    setSelectedClause(null);
    // Reset checkbox dropdown states
    setSelectedBulkRemarksOptions([]);
    setIsBulkCheckboxDropdownOpen(false);
  };

  // Checkbox dropdown handlers for bulk adequacy
  const handleBulkCheckboxChange = useCallback((option, isChecked) => {
    const optionText = typeof option === 'string' ? option : option.inadequate_remarks;
    
    if (isChecked) {
      setSelectedBulkRemarksOptions(prev => [...prev, option]);
    } else {
      setSelectedBulkRemarksOptions(prev => 
        prev.filter(item => {
          const itemText = typeof item === 'string' ? item : item.inadequate_remarks;
          return itemText !== optionText;
        })
      );
    }
  }, []);

  const handleClearAllBulkCheckboxes = useCallback(() => {
    setSelectedBulkRemarksOptions([]);
    setBulkAdequacyRemarks('');
  }, []);

  const handleBulkAdequacyRemarksChange = useCallback((newValue, isFromDropdown = false) => {
    setBulkAdequacyRemarks(newValue);
    
    if (!isFromDropdown && selectedBulkRemarksOptions.length > 0) {
      const selectedTexts = selectedBulkRemarksOptions.map(option => 
        typeof option === 'string' ? option : option.inadequate_remarks
      );
      
      if (!selectedTexts.some(text => newValue.includes(text))) {
        setSelectedBulkRemarksOptions([]);
      }
    }
  }, [selectedBulkRemarksOptions]);

  // Update remarks when checkbox selections change
  useEffect(() => {
    if (selectedBulkRemarksOptions.length > 0) {
      const selectedTexts = selectedBulkRemarksOptions.map(option => 
        typeof option === 'string' ? option : option.inadequate_remarks
      );
      
      const currentText = bulkAdequacyRemarks || '';
      const existingRemarks = currentText.split(',').map(s => s.trim()).filter(Boolean);
      
      const newRemarks = [...new Set([...existingRemarks, ...selectedTexts])];
      const updatedText = newRemarks.join(', ');
      
      if (updatedText !== currentText) {
        setBulkAdequacyRemarks(updatedText);
      }
    }
  }, [selectedBulkRemarksOptions, bulkAdequacyRemarks]);

  const handleBulkAdequacySubmit = async () => {
    if (!bulkAdequacyStatus || (bulkAdequacyStatus === 'Inadequate' && !bulkAdequacyRemarks?.trim())) {
      showNotification('Please fill all required fields', 'error');
      return;
    }

    setBulkAdequacyLoading(true);
    try {
      console.log('=== BULK EQUIPMENT ADEQUACY SUBMIT ===');
      console.log('Selected equipments:', selectedAdequacyEquipments);
      console.log('Bulk status:', bulkAdequacyStatus);
      console.log('Bulk remarks:', bulkAdequacyRemarks);
      console.log('Selected clause:', selectedClause);
      console.log('Available clauses:', clauseNo);
      console.log('AssessorId:', assessorId);
      console.log('AssessmentId:', assessmentId);
      console.log('UserTypeId:', localStorage.getItem('userTypeId'));
      
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      
      // Process each selected equipment
      for (const equipmentId of selectedAdequacyEquipments) {
        try {
          // Check if Record exists for this equipment
          const existingRecord = dancRecords.find(record => 
            record.sourceid && parseInt(record.sourceid) === parseInt(equipmentId)
          );
          
          if (existingRecord) {
            const NCtype = userTypeId === '4' ? 'Document Review' : 'Assessment';
            // Update existing Record
            const updateData = {
              subsectionName: 'Equipment Details',
              informationStatus: bulkAdequacyStatus,
              observation: bulkAdequacyStatus === 'Inadequate' 
                ? (bulkAdequacyRemarks || 'Document adequacy marked as Inadequate')
                : 'Document adequacy check completed',
              sourceid: equipmentId,
              isactive: 1,
               NCtype: NCtype,
              ...(selectedClause && { ClauseNo: selectedClause.DisplayName || selectedClause.ClauseNo })
            };
            
            console.log(`Updating Record for equipment ${equipmentId}:`, updateData);
            const response = await updateDANCBySourceId(formData.orgId, formData.appId, 'Equipment', equipmentId, assessmentId, assessorId, updateData);
            
            if (response.success) {
              console.log(`✅ Record updated for equipment ${equipmentId}`);
              successCount++;
            } else {
              throw new Error(response.message || 'Failed to Update');
            }
          } else {
            // Create new Record with userTypeId-based NCtype
            const NCtype = userTypeId === '4' ? 'Document Review' : 'Assessment';
            console.log(`🔍 EquipmentSection - Creating DANC record for userType ${userTypeId} with NCtype: ${NCtype}`);
            
            // Base data object
            const dancData = {
              Orgid: parseInt(formData.orgId),
              Appid: parseInt(formData.appId),
              SectionName: 'Equipment',
              SubsectionName: 'Equipment Details',
              InformationProvided: bulkAdequacyRemarks || '',
              InformationStatus: bulkAdequacyStatus,
              Observation: bulkAdequacyStatus === 'Inadequate' 
                ? (bulkAdequacyRemarks || 'Document adequacy marked as Inadequate')
                : 'Document adequacy check completed',
              sourceid: equipmentId,
              Insertedby: currentUserId,
              NCtype: NCtype,
              ...(selectedClause && { ClauseNo: selectedClause.DisplayName || selectedClause.ClauseNo }),
              // Conditional parameters for Assessment users (userType 6)
              ...(userTypeId === '6' && {
                assessmentId: assessmentId,
                assessorId: assessorId
              })
            };
            
            console.log(`Creating Record for equipment ${equipmentId}:`, dancData);
            const response = await insertDANC(dancData);
            
            if (response.success) {
              console.log(`✅ Record created for equipment ${equipmentId}`);
              successCount++;
            } else {
              throw new Error(response.message || 'Failed to create Record');
            }
          }
        } catch (error) {
          console.error(`Error processing equipment ${equipmentId}:`, error);
          errors.push(`Equipment ${equipmentId}: ${error.message}`);
          errorCount++;
        }
      }
      
      // Update adequacy checks locally for all selected equipments
      const updatedAdequacyChecks = { ...adequacyChecks };
      selectedAdequacyEquipments.forEach(equipmentId => {
        updatedAdequacyChecks[equipmentId] = {
          status: bulkAdequacyStatus,
          remarks: bulkAdequacyRemarks,
          isSubmitted: true,
          submittedAt: new Date().toISOString()
        };
      });
      setAdequacyChecks(updatedAdequacyChecks);
      
      // Show results
      if (successCount > 0) {
        showNotification(
          `Bulk adequacy check completed: ${successCount} equipment(s) processed successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}`, 
          errorCount > 0 ? 'warning' : 'success'
        );
      }
      
      if (errorCount > 0 && errors.length > 0) {
        console.error('Bulk adequacy submission errors:', errors);
      }
      
      // Trigger refresh to reload DA-NC data
      setRefreshTrigger(prev => prev + 1);
      
      // Clear selections and close modal
      setSelectedAdequacyEquipments([]);
      handleCloseBulkAdequacyModal();
      
    } catch (error) {
      console.error('Error submitting bulk adequacy check:', error);
      showNotification('Error submitting bulk adequacy check: ' + error.message, 'error');
    } finally {
      setBulkAdequacyLoading(false);
    }
  };

  const handleAdequacyEquipmentSelection = (equipmentId, isSelected) => {
    if (isSelected) {
      setSelectedAdequacyEquipments(prev => [...prev, equipmentId]);
    } else {
      setSelectedAdequacyEquipments(prev => prev.filter(id => id !== equipmentId));
    }
  };

  const handleSelectAllAdequacyEquipments = (isSelected) => {
    if (isSelected) {
      setSelectedAdequacyEquipments(existingEquipments.map(equipment => equipment.Id));
    } else {
      setSelectedAdequacyEquipments([]);
    }
  };

  // Download template with existing data
  const handleDownloadTemplate = async () => {
    try {
      setDownloadLoading(true);
      showNotification('Preparing Excel template...', 'info');

      // Define column headers for the Excel template
      const headers = [
        'Equipment ID',
        'Discipline',
        'UID of Equipment',
        'Name of Equipment',
        'make/company',
        'Serial No',
        'Model',
        'Year of Make',
        'Receipt Date',
        'Date of Installation',
        'Limit of Detection',
        'Last Calibration Date',
        'Calibration Due on',
        'Calibrated By',
        'In-house/Traceability',
        'Name of Calibration Agency(External)'
      ];

      // Prepare data array starting with headers
      const worksheetData = [headers];

      // Add existing equipment data if available
      if (existingEquipments && existingEquipments.length > 0) {
        existingEquipments.forEach((equipment, index) => {
          const row = [
            equipment.Equipmentid || '',
            formData.disciplineName || '',
            equipment.Uid || '',
            equipment.NameOfEquipment || '',
            equipment.MakeCompany || '',
            equipment.SerialNo || '',
            equipment.Model || '',
            equipment.YearOfMake || '',
            formatDateWithoutTimezone(equipment.ReceiptDate) || '',
            formatDateWithoutTimezone(equipment.DateOfInstallation) || '',
            equipment.LimitOfDetection || '',
            formatDateWithoutTimezone(equipment.DateOfLastCalibration) || '',
            formatDateWithoutTimezone(equipment.CalibrationDueOn) || '',
            equipment.CalibratedBy || '',
            equipment.InHouseTraceability || '',
            equipment.ExternalCalibrationAgency || ''
          ];
          worksheetData.push(row);
        });
      } else {
        // If no existing data, add instructional rows for better user experience
        
        // Add instruction row
        const instructionRow = [
          'Enter Equipment ID',           // A
          'Auto-filled',                  // B
          'Enter UID',                    // C
          'Enter Equipment Name',         // D
          'Enter Manufacturer',           // E
          'Enter Serial Number',          // F
          'Enter Model',                  // G
          'Enter Year (YYYY)',            // H
          'Date: DD/MM/YYYY',             // I
          'Date: DD/MM/YYYY',             // J
          'Enter Detection Limit',        // K
          'Date: DD/MM/YYYY',             // L
          'Date: DD/MM/YYYY',             // M
          'DROPDOWN: In-house OR External', // N - Calibrated By (emphasized)
          'Fill if In-house selected',    // O - Traceability
          'Fill if External selected'     // P - Agency Name
        ];
        worksheetData.push(instructionRow);
        
        // Add example rows showing both scenarios
        const inHouseExample = [
          'EQ001',
          formData.disciplineName || 'Medical',
          'UID001',
          'pH Meter',
          'ABC Instruments',
          'SN12345',
          'pH-100',
          '2023',
          '01/01/2023',
          '15/01/2023',
          '0.01 pH',
          '01/12/2023',
          '01/12/2024',
          'In-house',                     // Select this for in-house
          'NIST Traceable',               // Fill this when In-house
          ''                              // Leave empty when In-house
        ];
        worksheetData.push(inHouseExample);
        
        const externalExample = [
          'EQ002',
          formData.disciplineName || 'Medical',
          'UID002',
          'Balance',
          'XYZ Company',
          'SN67890',
          'BAL-200',
          '2022',
          '15/02/2022',
          '01/03/2022',
          '0.1 mg',
          '15/11/2023',
          '15/11/2024',
          'External',                     // Select this for external
          '',                             // Leave empty when External
          'Calibration Labs Inc.'         // Fill this when External
        ];
        worksheetData.push(externalExample);
        
        // Add more empty rows for data entry
        for (let i = 1; i <= 3; i++) {
          const emptyRow = Array(16).fill('');
          emptyRow[1] = formData.disciplineName || '';  // Pre-fill discipline
          worksheetData.push(emptyRow);
        }
      }

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      // Set column widths for better readability
      const columnWidths = [
        { wch: 15 },  // Equipment ID
        { wch: 20 },  // Discipline
        { wch: 20 },  // UID
        { wch: 30 },  // Equipment Name
        { wch: 25 },  // Manufacturer
        { wch: 15 },  // Serial No
        { wch: 15 },  // Model
        { wch: 12 },  // Year of Make
        { wch: 15 },  // Receipt Date
        { wch: 18 },  // Installation Date
        { wch: 18 },  // Limit of Detection
        { wch: 18 },  // Last Calibration
        { wch: 18 },  // Calibration Due
        { wch: 15 },  // Calibrated By
        { wch: 25 },  // In-house/Traceability
        { wch: 30 }   // External Agency
      ];
      worksheet['!cols'] = columnWidths;

      // Style the header row
      const headerRange = XLSX.utils.decode_range(worksheet['!ref']);
      for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!worksheet[cellAddress]) continue;
        
        worksheet[cellAddress].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "4472C4" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } }
          }
        };
      }

      // Style instruction and example rows if they exist (when no existing data)
      if (existingEquipments.length === 0) {
        // Style instruction row (row 2)
        for (let col = 0; col < headers.length; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: 1, c: col });
          if (!worksheet[cellAddress]) continue;
          
          worksheet[cellAddress].s = {
            font: { italic: true, color: { rgb: "666666" }, size: 9 },
            fill: { fgColor: { rgb: "F8F9FA" } },
            alignment: { horizontal: "center", vertical: "center", wrapText: true }
          };
        }
        
        // Style example rows (rows 3 and 4) with different colors
        const exampleRowColors = ["E8F5E8", "FFF2E8"];  // Light green and light orange
        for (let row = 2; row <= 3; row++) {
          for (let col = 0; col < headers.length; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
            if (!worksheet[cellAddress]) continue;
            
            worksheet[cellAddress].s = {
              font: { color: { rgb: "333333" } },
              fill: { fgColor: { rgb: exampleRowColors[row - 2] } },
              alignment: { horizontal: "left", vertical: "center" }
            };
          }
        }

        // Add special highlighting for calibration columns (columns N, O, P)
        const calibrationColumns = [13, 14, 15]; // Calibrated By, Traceability, Agency
        for (let row = 1; row <= 3; row++) {
          calibrationColumns.forEach(col => {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
            if (!worksheet[cellAddress]) return;
            
            if (!worksheet[cellAddress].s) worksheet[cellAddress].s = {};
            
            // Add border to highlight calibration logic
            worksheet[cellAddress].s.border = {
              top: { style: "medium", color: { rgb: "FF6B35" } },
              bottom: { style: "medium", color: { rgb: "FF6B35" } },
              left: { style: "medium", color: { rgb: "FF6B35" } },
              right: { style: "medium", color: { rgb: "FF6B35" } }
            };
          });
        }
      }

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Equipment Data');

      // Add data validation for Calibrated By dropdown
      // Create a separate validation values sheet
      const validationData = [
        ['CalibrationOptions'],
        ['In-house'],
        ['External']
      ];
      const validationSheet = XLSX.utils.aoa_to_sheet(validationData);
      XLSX.utils.book_append_sheet(workbook, validationSheet, 'ValidationData');

      // Add data validation to the main worksheet
      // Note: This creates the structure, but Excel will need to recognize it
      if (!worksheet['!dataValidation']) {
        worksheet['!dataValidation'] = [];
      }

      // Find the range for Calibrated By column (column N, index 13)
      const lastRow = worksheetData.length + 10; // Add extra rows for future entries
      const calibratedByColumn = 'N'; // Column N is "Calibrated By"
      
      // Add validation for multiple rows
      for (let row = 2; row <= lastRow; row++) {
        const cellRef = `${calibratedByColumn}${row}`;
        
        worksheet['!dataValidation'].push({
          ref: cellRef,
          type: 'list',
          allowBlank: true,
          formula1: 'ValidationData!$A$2:$A$3',
          showInputMessage: true,
          promptTitle: 'Calibrated By',
          prompt: 'Select In-house or External',
          showErrorMessage: true,
          errorTitle: 'Invalid Selection',
          error: 'Please select In-house or External from the dropdown'
        });
      }

      // Alternative: Add dropdown using Excel's native validation format
      // This creates the XML structure that Excel recognizes
      const range = `N2:N${lastRow}`;
      if (!worksheet['!ref']) {
        worksheet['!ref'] = XLSX.utils.encode_range({s: {c: 0, r: 0}, e: {c: 15, r: lastRow}});
      }

      // Add validation using Excel-compatible format
      worksheet['!dataValidation'] = [{
        ref: range,
        type: 'list',
        operator: 'between',
        formula1: '"In-house,External"',
        allowBlank: true,
        showInputMessage: true,
        promptTitle: 'Calibration Type',
        prompt: 'Choose calibration type:\n• In-house: Fill Traceability column\n• External: Fill Agency column',
        showErrorMessage: true,
        errorTitle: 'Invalid Entry',
        error: 'Please select either "In-house" or "External"'
      }];

      // Create instructions worksheet
      const instructionData = [
        ['NABL Equipment Data Template - Instructions'],
        [''],
        ['How to use the Calibrated By dropdown logic:'],
        [''],
        ['1. CALIBRATED BY COLUMN (Column N):'],
        ['   - Enter exactly: "In-house" OR "External"'],
        ['   - This determines which additional column to fill'],
        [''],
        ['2. IF YOU SELECT "In-house":'],
        ['   - Fill Column O: "In-house/Traceability"'],
        ['   - Example: "NIST Traceable", "ISO/IEC 17025", "Primary Standard"'],
        ['   - Leave Column P: "External Agency" EMPTY'],
        [''],
        ['3. IF YOU SELECT "External":'],
        ['   - Fill Column P: "Name of Calibration Agency(External)"'],
        ['   - Example: "Calibration Labs Inc.", "XYZ Metrology Services"'],
        ['   - Leave Column O: "In-house/Traceability" EMPTY'],
        [''],
        ['4. EXAMPLES IN TEMPLATE:'],
        ['   - Row 3 (Green): Shows In-house calibration example'],
        ['   - Row 4 (Orange): Shows External calibration example'],
        [''],
        ['5. DATE FORMATS:'],
        ['   - Use DD/MM/YYYY format for all dates'],
        ['   - Example: 25/12/2023 for December 25, 2023'],
        [''],
        ['6. REQUIRED FIELDS:'],
        ['   - Equipment ID and Equipment Name are mandatory'],
        ['   - All other fields are optional but recommended'],
        [''],
        ['7. BULK Upload:'],
        ['   - Save this file after entering data'],
        ['   - Upload back to the system for bulk processing'],
        ['   - Only existing equipment will be updated']
      ];
      
      const instructionSheet = XLSX.utils.aoa_to_sheet(instructionData);
      
      // Style the instruction sheet
      instructionSheet['!cols'] = [{ wch: 80 }];
      
      // Style the title
      if (instructionSheet['A1']) {
        instructionSheet['A1'].s = {
          font: { bold: true, size: 16, color: { rgb: "2E5B8A" } },
          alignment: { horizontal: "center" }
        };
      }
      
      // Style section headers
      const sectionHeaders = ['A5', 'A9', 'A13', 'A18', 'A21', 'A24', 'A27'];
      sectionHeaders.forEach(cell => {
        if (instructionSheet[cell]) {
          instructionSheet[cell].s = {
            font: { bold: true, color: { rgb: "D63384" } }
          };
        }
      });
      
      XLSX.utils.book_append_sheet(workbook, instructionSheet, 'Instructions');

      // Generate filename with current date and discipline
      const currentDate = new Date().toISOString().split('T')[0];
      const disciplineName = formData.disciplineName ? formData.disciplineName.replace(/[^a-zA-Z0-9]/g, '_') : 'Equipment';
      const filename = `${disciplineName}_Equipment_Template_${currentDate}.xlsx`;

      // Download the file
      XLSX.writeFile(workbook, filename);

      showNotification(
        `Smart template downloaded! ${existingEquipments.length > 0 ? `Includes ${existingEquipments.length} existing equipment(s).` : 'Includes calibration dropdown guidance and examples.'} Check Instructions tab for calibration logic.`, 
        'success'
      );

    } catch (error) {
      console.error('Error generating template:', error);
      showNotification('Error generating template: ' + error.message, 'error');
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      {/* Header Section */}
      <Paper
        sx={{
          mb: 3,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            py: 2,
            px: 2,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            borderRadius: 1,
            mb: 0,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            🔧 Testing Laboratory Equipment Management
            {isEquipmentSectionUnfrozen && (
              <Chip
                label="Unfrozen"
                color="warning"
                size="small"
                sx={{ ml: 2 }}
              />
            )}
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Display current discipline info */}
          {formData.disciplineName && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Current Discipline/Group: {formData.disciplineName}
                {" - "}
                {formData.groupName}
              </Typography>
            </Alert>
          )}

          {/* Show unfrozen status warning */}
          {isEquipmentSectionUnfrozen && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography>
                ⚠️ This section is currently unfrozen and allows editing even
                after submission.
              </Typography>
            </Alert>
          )}

          {/* Tabs for different sections */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              mb: 3,
              "& .MuiTab-root": {
                fontWeight: 600,
                fontSize: "1rem",
              },
            }}
          >
            <Tab label="📖 Instructions" />
            {(applicationStage === null ||
              applicationStage === "" ||
              applicationStage === "18a" ||
              applicationStage === "18" ||
              applicationStage === "18b" ||
              isEquipmentSectionUnfrozen) && (
              <Tab label="📝 Add Equipment (Master)" />
            )}
            {(applicationStage === null ||
              applicationStage === "" ||
              applicationStage === "18a" ||
              applicationStage === "18" ||
              applicationStage === "18b" ||
              isEquipmentSectionUnfrozen) && <Tab label="📤 Bulk Upload" />}
            <Tab label="📋 Existing Equipments" />
          </Tabs>

          {/* Tab 0: Instructions */}
          {activeTab === 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{ mb: 3, color: "#1976d2", fontWeight: 600 }}
                >
                  📋 Equipment — Step-by-step Instructions
                </Typography>

                {instructionsLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      py: 2,
                    }}
                  >
                    <CircularProgress size={24} sx={{ mr: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      Loading instructions...
                    </Typography>
                  </Box>
                ) : instructions.length > 0 ? (
                  <Box
                    sx={{
                      pl: 2,
                      "& p": {
                        mb: 1,
                        fontSize: "0.95rem",
                        lineHeight: 1.6,
                        color: "#424242",
                      },
                    }}
                  >
                    {instructions.map((instruction, index) => (
                      <Typography key={instruction.id} component="p">
                        {index + 1}. {instruction.instruction}
                      </Typography>
                    ))}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      "& .section": { mb: 3 },
                      "& .section-title": {
                        fontWeight: 600,
                        color: "#334155",
                        mb: 1,
                        fontSize: "1.1rem",
                      },
                      "& .step-list": {
                        pl: 3,
                        "& li": {
                          mb: 1,
                          fontSize: "0.95rem",
                          lineHeight: 1.6,
                          color: "#424242",
                        },
                      },
                      "& .tip": {
                        mt: 1,
                        p: 2,
                        backgroundColor: "#f0f9ff",
                        borderLeft: "4px solid #0ea5e9",
                        borderRadius: "0 4px 4px 0",
                        fontSize: "0.9rem",
                        fontStyle: "italic",
                        color: "#0369a1",
                      },
                    }}
                  >
                    <Box className="section">
                      <Typography className="section-title">
                        A. Add Equipment (single entry)
                      </Typography>
                      <ol className="step-list">
                        <li>Go to Equipment.</li>
                        <li>Click Add New.</li>
                        <li>
                          In Model Equipment & Scope, search and select the
                          closest matching Equipment.
                        </li>
                        <li>
                          The form will prefill standard fields (as per model).
                        </li>
                        <li>
                          Enter the remaining details as applicable (per the
                          on-screen form/template).
                        </li>
                        <li>Save.</li>
                      </ol>
                      <Box className="tip">
                        <strong>Tip:</strong> If you can't find the item in the
                        Model list, use the Other section to add it manually
                        (entries here are added one at a time).
                      </Box>
                    </Box>

                    <Box className="section">
                      <Typography className="section-title">
                        B. Add Equipment (bulk upload)
                      </Typography>
                      <ol className="step-list">
                        <li>
                          In Bulk Upload, click Download Excel to get the
                          pre-filled template (based on what you selected from
                          the Model list).
                        </li>
                        <li>
                          Open the file and complete only the empty fields as
                          per the column instructions in the template.
                        </li>
                        <li>
                          Save the file and Upload it back in Bulk Upload.
                        </li>
                        <li>
                          Review the validation summary; fix any flagged rows
                          and re-upload if needed.
                        </li>
                      </ol>
                    </Box>

                    <Box className="section">
                      <Typography className="section-title">
                        C. Edit or review existing Equipment
                      </Typography>
                      <ol className="step-list">
                        <li>Open View Existing.</li>
                        <li>Use search/filters to locate the entry.</li>
                        <li>Click Edit, make changes, and Save.</li>
                      </ol>
                    </Box>

                    <Box className="section">
                      <Typography className="section-title">
                        D. When the Equipment isn't in the Model list
                      </Typography>
                      <ol className="step-list">
                        <li>Go to Other → Add Equipment.</li>
                        <li>Enter the details manually (one-by-one).</li>
                        <li>
                          After save, the entry becomes available for use like
                          any other Equipment.
                        </li>
                      </ol>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Tab 1: Equipment Master List */}
          {(applicationStage === null ||
            applicationStage === "" ||
            applicationStage === "18a" ||
            applicationStage === "18" ||
            applicationStage === "18b" ||
            isEquipmentSectionUnfrozen) &&
            activeTab === 1 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ color: "#334155" }}>
                        Add Equipments from Master Data
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <TextField
                        size="small"
                        placeholder="Search equipments..."
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        sx={{ width: 250 }}
                      />
                      {/* <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Sub Group</InputLabel>
                        <Select
                          value={selectedSubGroup}
                          onChange={(e) => handleSearchSubGroup(e.target.value)}
                          label="Sub Group"
                          disabled={subGroupsLoading}
                        >
                          <MenuItem value="">All</MenuItem>
                          {subGroups.map((group) => (
                            <MenuItem key={group.sGrpId} value={group.sGrpId}>
                              {group.subGrpName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl> */}
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel>Per Page</InputLabel>
                        <Select
                          value={pageSize}
                          onChange={(e) => {
                            setPageSize(e.target.value);
                            setCurrentPage(1);
                            loadEquipmentMasterData(1, searchQuery);
                          }}
                          label="Per Page"
                        >
                          <MenuItem value={5}>5</MenuItem>
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={25}>25</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                        </Select>
                      </FormControl>
                      {/* <Button
                      variant="contained"
                      startIcon={<AddOtherIcon />}
                      onClick={handleAddOtherEquipment}
                      size="small"
                      sx={{ ml: 1 }}
                    >
                      Add Other Equipment
                    </Button> */}
                    </Box>
                  </Box>

                  {equipmentMasterLoading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 4 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <Box
                        sx={{
                          mb: 2,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Showing {equipmentMasterData.length} of {totalRecords}{" "}
                          results
                        </Typography>
                        <Typography variant="body2" color="primary">
                          {selectedEquipments.length} selected
                        </Typography>
                      </Box>

                      <TableContainer
                        component={Paper}
                        sx={{ maxHeight: 600, border: "1px solid #e2e8f0" }}
                      >
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={
                                    equipmentMasterData.length > 0 &&
                                    equipmentMasterData.every((equipment) =>
                                      selectedEquipments.includes(
                                        equipment.eqid,
                                      ),
                                    )
                                  }
                                  indeterminate={
                                    equipmentMasterData.some((equipment) =>
                                      selectedEquipments.includes(
                                        equipment.eqid,
                                      ),
                                    ) &&
                                    !equipmentMasterData.every((equipment) =>
                                      selectedEquipments.includes(
                                        equipment.eqid,
                                      ),
                                    )
                                  }
                                  onChange={(e) =>
                                    handleSelectAll(e.target.checked)
                                  }
                                />
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>
                                Equipment ID
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>
                                Equipment Name
                              </TableCell>
                              {/* <TableCell sx={{ fontWeight: 600 }}>
                                Sub Group
                              </TableCell> */}
                              {/* <TableCell sx={{ fontWeight: 600 }}>Discipline</TableCell> */}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {equipmentMasterData.map((equipment, index) => (
                              <TableRow
                                key={equipment.eqid}
                                sx={{
                                  backgroundColor:
                                    index % 2 === 0 ? "#ffffff" : "#f8fafc",
                                  "&:hover": {
                                    backgroundColor: "#e0f2fe",
                                    transition:
                                      "background-color 0.2s ease-in-out",
                                  },
                                }}
                              >
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    checked={selectedEquipments.includes(
                                      equipment.eqid,
                                    )}
                                    onChange={(e) =>
                                      handleEquipmentSelection(
                                        equipment.eqid,
                                        e.target.checked,
                                      )
                                    }
                                  />
                                </TableCell>
                                <TableCell
                                  sx={{ fontWeight: "500", color: "#1976d2" }}
                                >
                                  {equipment.equipmentid}
                                </TableCell>
                                <TableCell>{equipment.equipmentname}</TableCell>
                                {/* <TableCell>{equipment.subGroupName}</TableCell> */}
                                {/* <TableCell>{formData.disciplineName}</TableCell> */}
                              </TableRow>
                            ))}
                            {equipmentMasterData.length === 0 &&
                              !equipmentMasterLoading && (
                                <TableRow>
                                  <TableCell
                                    colSpan={4}
                                    align="center"
                                    sx={{ py: 4 }}
                                  >
                                    <Typography color="text.secondary">
                                      {searchQuery
                                        ? "No equipment data found for your search"
                                        : 'Click "Load Equipment Master" to view available equipment entries'}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            mt: 2,
                            gap: 1,
                          }}
                        >
                          <Button
                            size="small"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </Button>

                          <Box sx={{ display: "flex", gap: 1 }}>
                            {Array.from(
                              { length: Math.min(5, totalPages) },
                              (_, i) => {
                                const pageNum =
                                  Math.max(1, currentPage - 2) + i;
                                if (pageNum > totalPages) return null;

                                return (
                                  <Button
                                    key={pageNum}
                                    size="small"
                                    variant={
                                      pageNum === currentPage
                                        ? "contained"
                                        : "outlined"
                                    }
                                    onClick={() => handlePageChange(pageNum)}
                                    sx={{ minWidth: 40 }}
                                  >
                                    {pageNum}
                                  </Button>
                                );
                              },
                            )}
                          </Box>

                          <Button
                            size="small"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </Button>

                          <Typography variant="body2" sx={{ ml: 2 }}>
                            Page {currentPage} of {totalPages}
                          </Typography>
                        </Box>
                      )}

                      {/* Action Buttons */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 2,
                          mt: 3,
                        }}
                      >
                        <Button
                          onClick={() =>
                            loadEquipmentMasterData(currentPage, searchQuery)
                          }
                          variant="outlined"
                          startIcon={<RefreshIcon />}
                        >
                          Load Equipment Master
                        </Button>
                        {(applicationStage === null ||
                          applicationStage === "" ||
                          applicationStage === "18a" ||
                          isEquipmentSectionUnfrozen) && (
                          <Button
                            onClick={handleAddSelectedEquipments}
                            variant="contained"
                            disabled={selectedEquipments.length === 0}
                            startIcon={<SaveIcon />}
                          >
                            Add Selected ({selectedEquipments.length})
                          </Button>
                        )}
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

          {/* Tab 2: Bulk Upload */}
          {(applicationStage === null ||
            applicationStage === "" ||
            applicationStage === "18a" ||
            applicationStage === "18" ||
            applicationStage === "18b" ||
            isEquipmentSectionUnfrozen) &&
            activeTab === 2 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: "#334155" }}>
                    Bulk Upload Equipments
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    Upload multiple equipment entries using an Excel or CSV
                    file. You'll see a preview before uploading.
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <input
                        accept=".xlsx,.xls,.csv"
                        style={{ display: "none" }}
                        id="bulk-upload-input"
                        type="file"
                        onChange={handleBulkFileUpload}
                      />
                      <label htmlFor="bulk-upload-input">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<UploadIcon />}
                          fullWidth
                          sx={{ py: 1.5 }}
                        >
                          Select File for Preview
                        </Button>
                      </label>
                      {uploadFile && (
                        <Typography
                          variant="body2"
                          sx={{ mt: 1, color: "green" }}
                        >
                          Selected: {uploadFile.name}
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Button
                        variant="contained"
                        onClick={() => {
                          if (uploadFile) {
                            setPreviewModalOpen(true);
                          } else {
                            setSnackbar({
                              open: true,
                              message: "Please select a file first",
                              severity: "warning",
                            });
                          }
                        }}
                        disabled={!uploadFile || loading}
                        startIcon={
                          loading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <ViewIcon />
                          )
                        }
                        fullWidth
                        sx={{ py: 1.5 }}
                      >
                        {loading ? "Processing..." : "Show Preview"}
                      </Button>
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        variant="text"
                        startIcon={
                          downloadLoading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <DownloadIcon />
                          )
                        }
                        onClick={handleDownloadTemplate}
                        disabled={downloadLoading}
                      >
                        {downloadLoading
                          ? "Preparing Template..."
                          : "Download Template with Existing Data"}
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

          {/* Tab 3: Existing Equipments (index changes based on stage) */}
          {(((applicationStage === null ||
            applicationStage === "" ||
            applicationStage === "18a" ||
            applicationStage === "18" ||
            applicationStage === "18b" ||
            isEquipmentSectionUnfrozen) &&
            activeTab === 3) ||
            (applicationStage !== null &&
              applicationStage !== "" &&
              applicationStage !== "18" &&
              applicationStage !== "18a" &&
              applicationStage !== "18b" &&
              !isEquipmentSectionUnfrozen &&
              activeTab === 1)) && (
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ color: "#334155" }}>
                      Existing Equipments ({existingEquipments.length})
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    {selectedForDelete.length > 0 &&
                      (applicationStage === null ||
                        applicationStage === "18a" ||
                        applicationStage === "" ||
                        isEquipmentSectionUnfrozen) && (
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={handleMultipleDelete}
                          size="small"
                          sx={{
                            animation: "fadeIn 0.3s ease-in-out",
                            "@keyframes fadeIn": {
                              from: {
                                opacity: 0,
                                transform: "translateY(-10px)",
                              },
                              to: { opacity: 1, transform: "translateY(0)" },
                            },
                          }}
                        >
                          Delete Selected ({selectedForDelete.length})
                        </Button>
                      )}

                    {/* Bulk Adequacy Check Button - Show when equipments are selected */}
                    {selectedAdequacyEquipments.length > 0 && (
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleIcon />}
                        onClick={handleOpenBulkAdequacyModal}
                        size="small"
                        sx={{
                          animation: "fadeIn 0.3s ease-in-out",
                          backgroundColor: "#28a745",
                          "&:hover": {
                            backgroundColor: "#218838",
                          },
                          "@keyframes fadeIn": {
                            from: {
                              opacity: 0,
                              transform: "translateY(-10px)",
                            },
                            to: { opacity: 1, transform: "translateY(0)" },
                          },
                        }}
                      >
                        Mark Adequacy ({selectedAdequacyEquipments.length})
                      </Button>
                    )}

                    {/* QR Codes Download Button - Show when equipments exist */}
                    {/* {existingEquipments.length > 0 && (
                      <Button
                        variant="outlined"
                        startIcon={pdfDownloading ? <CircularProgress size={16} /> : <PdfIcon />}
                        onClick={downloadQRCodesPDF}
                        disabled={pdfDownloading}
                        size="small"
                        sx={{
                          color: '#1976d2',
                          borderColor: '#1976d2',
                          '&:hover': {
                            backgroundColor: '#e3f2fd',
                            borderColor: '#1565c0'
                          }
                        }}
                      >
                        {pdfDownloading ? 'Generating...' : `Download QR Codes (${existingEquipments.length})`}
                      </Button>
                    )} */}

                    {/* Add Equipment buttons - Only show when stage is null */}
                    {(applicationStage === null ||
                      applicationStage === "18a" ||
                      isEquipmentSectionUnfrozen) && (
                      <>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleAddEquipment}
                          size="small"
                        >
                          Add Equipment
                        </Button>
                        {/* <Button
                          variant="contained"
                          color="secondary"
                          startIcon={<AddIcon />}
                          onClick={handleAddOtherEquipment}
                          size="small"
                          sx={{ ml: 1 }}
                        >
                          Add Other Equipment
                        </Button> */}
                      </>
                    )}
                  </Box>
                </Box>

                {existingEquipmentsLoading ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 4 }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <TableContainer
                    component={Paper}
                    sx={{
                      maxHeight: 600,
                      boxShadow: "none",
                      border: "1px solid #e2e8f0",
                      overflowX: "auto",
                    }}
                  >
                    <Table stickyHeader sx={{ minWidth: 2900 }}>
                      <TableHead>
                        <TableRow
                          sx={{
                            background:
                              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          }}
                        >
                          {/* Delete checkbox - Only show when stage is null */}
                          {(applicationStage === null ||
                            applicationStage === "18a" ||
                            applicationStage === "" ||
                            isEquipmentSectionUnfrozen) && (
                            <TableCell
                              sx={{ backgroundColor: "transparent" }}
                              padding="checkbox"
                            >
                              <Checkbox
                                checked={
                                  selectedForDelete.length ===
                                    existingEquipments.length &&
                                  existingEquipments.length > 0
                                }
                                indeterminate={
                                  selectedForDelete.length > 0 &&
                                  selectedForDelete.length <
                                    existingEquipments.length
                                }
                                onChange={(e) =>
                                  handleSelectAllForDelete(e.target.checked)
                                }
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  mt: 1,
                                  fontSize: "0.7rem",
                                  color: "#fff",
                                }}
                              >
                                Delete
                              </Typography>
                            </TableCell>
                          )}

                          {/* Adequacy checkbox - Show only when stage is 18/18b/20 AND userTypeId is 4 or 6 */}
                          {(applicationStage === "18" ||
                            applicationStage === "18b" ||
                            applicationStage === "20") &&
                            (localStorage.getItem("userTypeId") === "4" ||
                              localStorage.getItem("userTypeId") === "6") && (
                              <TableCell
                                sx={{ backgroundColor: "#667eea" }}
                                padding="checkbox"
                              >
                                <Checkbox
                                  checked={
                                    selectedAdequacyEquipments.length ===
                                      existingEquipments.length &&
                                    existingEquipments.length > 0
                                  }
                                  indeterminate={
                                    selectedAdequacyEquipments.length > 0 &&
                                    selectedAdequacyEquipments.length <
                                      existingEquipments.length
                                  }
                                  onChange={(e) =>
                                    handleSelectAllAdequacyEquipments(
                                      e.target.checked,
                                    )
                                  }
                                  sx={{ color: "#fafafaff" }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    mt: 1,
                                    fontSize: "0.7rem",
                                    color: "#ffffffff",
                                  }}
                                >
                                  Adequacy
                                </Typography>
                              </TableCell>
                            )}
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#fff",
                              border: "1px solid %fff",
                              background: "#667eea",
                              minWidth: 60,
                            }}
                          >
                            S No.
                          </TableCell>
                          {(applicationStage === "18" ||
                            applicationStage === "18a" ||
                            applicationStage === "18b" ||
                            applicationStage === "20") && (
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: "#fff",
                                border: "1px solid %fff",
                                background: "#667eea",
                                minWidth: 120,
                              }}
                              align="center"
                            >
                              Adequacy Status
                            </TableCell>
                          )}
                          {/* <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#fff",
                              border: "1px solid %fff",
                              background: "#667eea",
                              minWidth: 120,
                            }}
                          >
                            Sub Group
                          </TableCell> */}
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#fff",
                              border: "1px solid %fff",
                              background: "#667eea",
                              minWidth: 120,
                            }}
                          >
                            Equipment ID
                          </TableCell>
                          {/* <TableCell sx={{ fontWeight: 600, color:'#fff', border:'1px solid %fff', background:'#667eea', minWidth: 120 }}>Discipline</TableCell> */}
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#fff",
                              border: "1px solid %fff",
                              background: "#667eea",
                              minWidth: 150,
                            }}
                          >
                            UID of Equipment
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#fff",
                              border: "1px solid %fff",
                              background: "#667eea",
                              minWidth: 200,
                            }}
                          >
                            Name of Equipment
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#fff",
                              border: "1px solid %fff",
                              background: "#667eea",
                              minWidth: 150,
                            }}
                          >
                            make/company
                          </TableCell>
                          {/* <TableCell sx={{ fontWeight: 600, color:'#fff', border:'1px solid %fff', background:'#667eea', minWidth: 100 }}>Upload IQ</TableCell>
                          <TableCell sx={{ fontWeight: 600, color:'#fff', border:'1px solid %fff', background:'#667eea', minWidth: 100 }}>Upload OQ</TableCell>
                          <TableCell sx={{ fontWeight: 600, color:'#fff', border:'1px solid %fff', background:'#667eea', minWidth: 100 }}>Upload PQ</TableCell> */}
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#fff",
                              border: "1px solid %fff",
                              background: "#667eea",
                              minWidth: 120,
                            }}
                          >
                            Serial No
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#fff",
                              border: "1px solid %fff",
                              background: "#667eea",
                              minWidth: 100,
                            }}
                            align="center"
                          >
                            QR Code
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#fff",
                              border: "1px solid %fff",
                              background: "#667eea",
                              minWidth: 100,
                            }}
                          >
                            Model
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#fff",
                              border: "1px solid %fff",
                              background: "#667eea",
                              minWidth: 100,
                            }}
                          >
                            Year of Make
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#fff",
                              border: "1px solid %fff",
                              background: "#667eea",
                              minWidth: 120,
                            }}
                          >
                            Receipt Date
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#fff",
                              border: "1px solid %fff",
                              background: "#667eea",
                              minWidth: 140,
                            }}
                          >
                            Date of Installation
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#fff",
                              border: "1px solid %fff",
                              background: "#667eea",
                              minWidth: 140,
                            }}
                          >
                            Limit of Detection
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#fff",
                              border: "1px solid %fff",
                              background: "#667eea",
                              minWidth: 140,
                            }}
                          >
                            Last Calibration Date
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#fff",
                              border: "1px solid %fff",
                              background: "#667eea",
                              minWidth: 140,
                            }}
                          >
                            Calibration Due on
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#fff",
                              border: "1px solid %fff",
                              background: "#667eea",
                              minWidth: 120,
                            }}
                          >
                            Calibrated By
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#fff",
                              border: "1px solid %fff",
                              background: "#667eea",
                              minWidth: 150,
                            }}
                          >
                            In-house/Traceability
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#fff",
                              border: "1px solid %fff",
                              background: "#667eea",
                              minWidth: 200,
                            }}
                          >
                            Name of Calibration Agency(External)
                          </TableCell>
                          {/* {(applicationStage === null || applicationStage === '18a' || applicationStage === "" || isSectionUnfrozen('Equipment')) && (
                          <TableCell sx={{ fontWeight: 600, color:'#fff', border:'1px solid %fff', background:'#667eea', minWidth: 120 }} align="center">Action</TableCell>
                          )} */}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {existingEquipments.map((equipment, index) => (
                          <TableRow
                            key={equipment.Id}
                            sx={{
                              backgroundColor:
                                index % 2 === 0 ? "#ffffff" : "#f8fafc",
                              "&:hover": {
                                backgroundColor: "#e0f2fe",
                                transition: "background-color 0.2s ease-in-out",
                              },
                            }}
                          >
                            {/* Delete checkbox - Only show when stage is null */}
                            {(applicationStage === null ||
                              applicationStage === "18a" ||
                              applicationStage === "" ||
                              isEquipmentSectionUnfrozen) && (
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedForDelete.includes(
                                    equipment.Id,
                                  )}
                                  onChange={(e) =>
                                    handleSelectForDelete(
                                      equipment.Id,
                                      e.target.checked,
                                    )
                                  }
                                />
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    justifyContent: "center",
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleEditEquipment(equipment);
                                    }}
                                    sx={{ color: "#1976d2" }}
                                    title="Edit"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>

                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleDeleteEquipment(equipment.Id);
                                    }}
                                    sx={{ color: "#d32f2f" }}
                                    title="Delete"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            )}

                            {/* Adequacy checkbox - Show only when stage is 18/18b/20 AND userTypeId is 4 or 6 */}
                            {(applicationStage === "18" ||
                              applicationStage === "18b" ||
                              applicationStage === "20") &&
                              (localStorage.getItem("userTypeId") === "4" ||
                                localStorage.getItem("userTypeId") === "6") && (
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    checked={selectedAdequacyEquipments.includes(
                                      equipment.Id,
                                    )}
                                    onChange={(e) =>
                                      handleAdequacyEquipmentSelection(
                                        equipment.Id,
                                        e.target.checked,
                                      )
                                    }
                                    sx={{
                                      color:
                                        adequacyChecks[equipment.Id]?.status ===
                                        "Adequate"
                                          ? "#28a745"
                                          : "#007bff",
                                      "&.Mui-checked": {
                                        color:
                                          adequacyChecks[equipment.Id]
                                            ?.status === "Adequate"
                                            ? "#28a745"
                                            : "#007bff",
                                      },
                                    }}
                                  />
                                  {adequacyChecks[equipment.Id]
                                    ?.isSubmitted && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        mt: 0.5,
                                      }}
                                    >
                                      <CheckCircleIcon
                                        sx={{
                                          fontSize: "0.8rem",
                                          color:
                                            adequacyChecks[equipment.Id]
                                              ?.status === "Adequate"
                                              ? "#28a745"
                                              : "#ffc107",
                                        }}
                                      />
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          ml: 0.5,
                                          fontSize: "0.6rem",
                                          color:
                                            adequacyChecks[equipment.Id]
                                              ?.status === "Adequate"
                                              ? "#28a745"
                                              : "#ffc107",
                                        }}
                                      >
                                        {adequacyChecks[equipment.Id]?.status}
                                      </Typography>
                                    </Box>
                                  )}
                                </TableCell>
                              )}
                            <TableCell
                              sx={{ fontWeight: "bold", color: "#1976d2" }}
                            >
                              {index + 1}
                            </TableCell>
                            {/* Adequacy Status Column - Show only when stage is 18/18a/18b/20 */}
                            {(applicationStage === "18" ||
                              applicationStage === "18a" ||
                              applicationStage === "18b" ||
                              applicationStage === "20") && (
                              <TableCell align="center">
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  {(() => {
                                    const statusData = getEquipmentStatus(
                                      equipment.Id,
                                    );
                                    return (
                                      <>
                                        {statusData.status !==
                                        "Not Assessed" ? (
                                          <Chip
                                            icon={
                                              statusData.status ===
                                              "Adequate" ? (
                                                <CheckCircleIcon
                                                  sx={{ fontSize: 16 }}
                                                />
                                              ) : statusData.status ===
                                                "Inadequate" ? (
                                                <ErrorIcon
                                                  sx={{ fontSize: 16 }}
                                                />
                                              ) : statusData.status ===
                                                "To be Verify" ? (
                                                <VerifyIcon
                                                  sx={{ fontSize: 16 }}
                                                />
                                              ) : null
                                            }
                                            label={statusData.status}
                                            color={
                                              statusData.status === "Adequate"
                                                ? "success"
                                                : statusData.status ===
                                                    "Inadequate"
                                                  ? "error"
                                                  : statusData.status ===
                                                      "To be Verify"
                                                    ? "info"
                                                    : "default"
                                            }
                                            size="small"
                                            sx={{
                                              fontWeight: 600,
                                              ...(statusData.status ===
                                                "To be Verify" && {
                                                backgroundColor: "#35dcdcff",
                                                color: "white",
                                                "& .MuiChip-icon": {
                                                  color: "white",
                                                },
                                              }),
                                            }}
                                          />
                                        ) : (
                                          <Chip
                                            label="Not Assessed"
                                            color="default"
                                            size="small"
                                            variant="outlined"
                                            sx={{ color: "#6c757d" }}
                                          />
                                        )}

                                        {/* Show clause number if available */}
                                        {statusData.ClauseNo && (
                                          <Chip
                                            label={`Clause: ${statusData.ClauseNo}`}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                              fontSize: "0.7rem",
                                              height: "20px",
                                              color: "#1976d2",
                                              borderColor: "#1976d2",
                                              backgroundColor: "#f3f4f6",
                                            }}
                                          />
                                        )}

                                        {/* Show observation/remarks for Inadequate status */}
                                        {statusData.status === "Inadequate" &&
                                          statusData.remarks && (
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: "#dc3545",
                                                fontSize: "0.7rem",
                                                textAlign: "center",
                                                maxWidth: 200,
                                                wordBreak: "break-word",
                                                lineHeight: 1.2,
                                                mt: 0.5,
                                                fontStyle: "italic",
                                              }}
                                            >
                                              "{statusData.remarks}"
                                            </Typography>
                                          )}
                                      </>
                                    );
                                  })()}
                                </Box>
                              </TableCell>
                            )}
                            {/* <TableCell>
                              {equipment.subGroupName || "N/A"}
                            </TableCell> */}
                            <TableCell
                              sx={{ fontWeight: "500", color: "#1976d2" }}
                            >
                              {equipment.Equipmentid || "N/A"}
                            </TableCell>
                            {/* <TableCell>{formData.disciplineName || 'N/A'}</TableCell> */}
                            <TableCell>{equipment.Uid || "N/A"}</TableCell>
                            <TableCell>{equipment.NameOfEquipment}</TableCell>
                            <TableCell>
                              {equipment.MakeCompany || "N/A"}
                            </TableCell>
                            {/* <TableCell>
                              {equipment.IQuploadFile ? (
                                <Button 
                                  size="small" 
                                  variant="outlined"
                                  onClick={() => handleFileView(equipment.IQuploadFile)}
                                >
                                  View
                                </Button>
                              ) : (
                                <Button size="small" variant="text" disabled>
                                  No File
                                </Button>
                              )}
                            </TableCell>
                            <TableCell>
                              {equipment.OQuploadFile ? (
                                <Button 
                                  size="small" 
                                  variant="outlined"
                                  onClick={() => handleFileView(equipment.OQuploadFile)}
                                >
                                  View
                                </Button>
                              ) : (
                                <Button size="small" variant="text" disabled>
                                  No File
                                </Button>
                              )}
                            </TableCell>
                            <TableCell>
                              {equipment.PQuploadFile ? (
                                <Button 
                                  size="small" 
                                  variant="outlined"
                                  onClick={() => handleFileView(equipment.PQuploadFile)}
                                >
                                  View
                                </Button>
                              ) : (
                                <Button size="small" variant="text" disabled>
                                  No File
                                </Button>
                              )}
                            </TableCell> */}
                            <TableCell>{equipment.SerialNo || "N/A"}</TableCell>
                            <TableCell align="center" sx={{ width: "100px" }}>
                              {(() => {
                                const equipmentId = equipment.Id;
                                const qrUrl = generateQRCodeUrl(equipmentId);
                                return qrUrl && equipmentId ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <QRCode
                                        value={qrUrl}
                                        size={60}
                                        level="M"
                                        style={{
                                          border: "2px solid #e0e0e0",
                                          borderRadius: "4px",
                                          padding: "4px",
                                        }}
                                      />
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontSize: "0.6rem",
                                          color: "#666",
                                        }}
                                      >
                                        QR Code
                                      </Typography>
                                    </Box>
                                  </Box>
                                ) : (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <QrCodeIcon
                                      sx={{ fontSize: 32, color: "#ccc" }}
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{ fontSize: "0.6rem", color: "#ccc" }}
                                    >
                                      No Data
                                    </Typography>
                                  </Box>
                                );
                              })()}
                            </TableCell>
                            <TableCell>{equipment.Model || "N/A"}</TableCell>
                            <TableCell>
                              {equipment.YearOfMake || "N/A"}
                            </TableCell>
                            <TableCell>
                              {formatDateWithoutTimezone(equipment.ReceiptDate)}
                            </TableCell>
                            <TableCell>
                              {formatDateWithoutTimezone(
                                equipment.DateOfInstallation,
                              )}
                            </TableCell>
                            <TableCell>
                              {equipment.LimitOfDetection || "N/A"}
                            </TableCell>
                            <TableCell>
                              {formatDateWithoutTimezone(
                                equipment.DateOfLastCalibration,
                              )}
                            </TableCell>
                            <TableCell>
                              {formatDateWithoutTimezone(
                                equipment.CalibrationDueOn,
                              )}
                            </TableCell>
                            <TableCell>
                              {equipment.CalibratedByType || "N/A"}
                            </TableCell>
                            <TableCell>
                              {equipment.InHouseCalibration || "N/A"}
                            </TableCell>
                            <TableCell>
                              {equipment.ExternalCalibrationAgency || "N/A"}
                            </TableCell>

                            {/* Show delete button only when stage is null */}
                            {/* {(applicationStage === null || applicationStage === '18a' || applicationStage === "" || isEquipmentSectionUnfrozen) && (
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleEditEquipment(equipment);
                                  }}
                                  sx={{ color: '#1976d2' }}
                                  title="Edit"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                
                               
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleDeleteEquipment(equipment.Id);
                                    }}
                                    sx={{ color: '#d32f2f' }}
                                    title="Delete"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                              </Box>
                            </TableCell>
                                )} */}
                          </TableRow>
                        ))}
                        {existingEquipments.length === 0 &&
                          !existingEquipmentsLoading && (
                            <TableRow>
                              <TableCell
                                colSpan={
                                  applicationStage === null ||
                                  applicationStage === "18a" ||
                                  applicationStage === ""
                                    ? 22
                                    : 21
                                }
                                align="center"
                                sx={{ py: 4 }}
                              >
                                <Typography color="text.secondary">
                                  {applicationStage === "18" ||
                                  applicationStage === "18b" ||
                                  applicationStage === "20"
                                    ? "No equipments available for adequacy check."
                                    : 'No equipments added yet. Click "Add Equipment" to get started.'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          )}
        </Box>
      </Paper>

      {/* Edit Equipment Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleCloseEditModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: "80vh" },
        }}
      >
        <DialogTitle>
          <Typography variant="h6">Edit Equipment</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
            {/* Read-only fields */}
            <Card sx={{ p: 2, backgroundColor: "#f8fafc" }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, color: "text.secondary" }}
              >
                Equipment Information (Read-only)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Equipment Name"
                    value={editReadOnlyData.equipmentName || ""}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{
                      "& .MuiInputBase-input": { color: "text.secondary" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Discipline Name"
                    value={editReadOnlyData.disciplineName || ""}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{
                      "& .MuiInputBase-input": { color: "text.secondary" },
                    }}
                  />
                </Grid>
              </Grid>
            </Card>

            {/* Editable fields */}
            <Card sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Editable Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Make/Company"
                    value={editFormData.makeCompany || ""}
                    onChange={(e) =>
                      setEditFormData((prev) => ({
                        ...prev,
                        makeCompany: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Model"
                    value={editFormData.model || ""}
                    onChange={(e) =>
                      setEditFormData((prev) => ({
                        ...prev,
                        model: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Serial Number"
                    value={editFormData.serialNo || ""}
                    onChange={(e) =>
                      setEditFormData((prev) => ({
                        ...prev,
                        serialNo: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Year of Make"
                    value={editFormData.yearOfMake || ""}
                    onChange={(e) =>
                      setEditFormData((prev) => ({
                        ...prev,
                        yearOfMake: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>
                {/* <Grid item xs={12} sm={6}>
                  <TextField
                    label="Range"
                    value={editFormData.range || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, range: e.target.value }))}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Accuracy"
                    value={editFormData.accuracy || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, accuracy: e.target.value }))}
                    fullWidth
                    size="small"
                  />
                </Grid> */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Date of Last Calibration"
                    type="date"
                    value={editFormData.dateOfLastCalibration || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Validate date of last calibration to ensure it's not in the future
                      if (value) {
                        const selectedDate = new Date(value);
                        const today = new Date();
                        today.setHours(23, 59, 59, 999); // Allow today's date

                        if (selectedDate > today) {
                          showNotification(
                            "Date of last calibration cannot be in the future. Please select today or a past date.",
                            "warning",
                          );
                          return; // Don't update the state with invalid date
                        }
                      }
                      setEditFormData((prev) => ({
                        ...prev,
                        dateOfLastCalibration: value,
                      }));
                    }}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      max: todayDate, // Last calibration cannot be in the future
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Calibration Due On"
                    type="date"
                    value={editFormData.calibrationDueOn || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Validate calibration due date to ensure it's not in the past
                      if (value) {
                        const selectedDate = new Date(value);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0); // Reset time to compare only dates

                        if (selectedDate < today) {
                          showNotification(
                            "Calibration due date cannot be in the past. Please select today or a future date.",
                            "warning",
                          );
                          return; // Don't update the state with invalid date
                        }
                      }
                      setEditFormData((prev) => ({
                        ...prev,
                        calibrationDueOn: value,
                      }));
                    }}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: todayDate,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Receipt Date"
                    type="date"
                    value={editFormData.receiptDate || ""}
                    onChange={(e) =>
                      setEditFormData((prev) => ({
                        ...prev,
                        receiptDate: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Calibrated By</InputLabel>
                    <Select
                      value={editFormData.calibratedByType || ""}
                      onChange={(e) =>
                        setEditFormData((prev) => ({
                          ...prev,
                          calibratedByType: e.target.value,
                        }))
                      }
                      label="Calibrated By"
                    >
                      <MenuItem value="In-house">In-house</MenuItem>
                      <MenuItem value="External">External</MenuItem>
                      <MenuItem value="Not Applicable">Not Applicable</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Conditional In-house text field */}
                {editFormData.calibratedByType === "In-house" && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="In-house Calibration Details"
                      value={editFormData.inHouseCalibration || ""}
                      onChange={(e) =>
                        setEditFormData((prev) => ({
                          ...prev,
                          inHouseCalibration: e.target.value,
                        }))
                      }
                      fullWidth
                      size="small"
                      placeholder="Enter in-house calibration details"
                    />
                  </Grid>
                )}

                {/* Conditional External text field */}
                {editFormData.calibratedByType === "External" && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Name of Calibration Agency(External)"
                      value={editFormData.externalCalibrationAgency || ""}
                      onChange={(e) =>
                        setEditFormData((prev) => ({
                          ...prev,
                          externalCalibrationAgency: e.target.value,
                        }))
                      }
                      fullWidth
                      size="small"
                      placeholder="Enter external agency name"
                    />
                  </Grid>
                )}

                {/* IQ Upload */}
                {/* <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>Upload IQ</Typography>
                    {editFormData.uploadIQ ? (
                      <Box>
                        <Button 
                          variant="outlined" 
                          size="small" 
                          onClick={() => handleFileView(editFormData.uploadIQ)}
                          sx={{ mr: 1 }}
                        >
                          View Current
                        </Button>
                        <input
                          accept=".pdf,.doc,.docx,.jpg,.png"
                          style={{ display: 'none' }}
                          id="replace-iq"
                          type="file"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleIQUpload(file);
                            }
                          }}
                        />
                        <label htmlFor="replace-iq">
                          <Button variant="text" component="span" size="small">
                            Replace
                          </Button>
                        </label>
                        {editFormData.uploadIQOriginalName && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            File: {editFormData.uploadIQOriginalName}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <>
                        <input
                          accept=".pdf,.doc,.docx,.jpg,.png"
                          style={{ display: 'none' }}
                          id="upload-iq"
                          type="file"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleIQUpload(file);
                            }
                          }}
                        />
                        <label htmlFor="upload-iq">
                          <Button variant="outlined" component="span" size="small" startIcon={<UploadIcon />}>
                            Upload IQ
                          </Button>
                        </label>
                      </>
                    )}
                  </Box>
                </Grid> */}

                {/* OQ Upload */}
                {/* <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>Upload OQ</Typography>
                    {editFormData.uploadOQ ? (
                      <Box>
                        <Button 
                          variant="outlined" 
                          size="small" 
                          onClick={() => handleFileView(editFormData.uploadOQ)}
                          sx={{ mr: 1 }}
                        >
                          View Current
                        </Button>
                        <input
                          accept=".pdf,.doc,.docx,.jpg,.png"
                          style={{ display: 'none' }}
                          id="replace-oq"
                          type="file"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleOQUpload(file);
                            }
                          }}
                        />
                        <label htmlFor="replace-oq">
                          <Button variant="text" component="span" size="small">
                            Replace
                          </Button>
                        </label>
                        {editFormData.uploadOQOriginalName && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            File: {editFormData.uploadOQOriginalName}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <>
                        <input
                          accept=".pdf,.doc,.docx,.jpg,.png"
                          style={{ display: 'none' }}
                          id="upload-oq"
                          type="file"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleOQUpload(file);
                            }
                          }}
                        />
                        <label htmlFor="upload-oq">
                          <Button variant="outlined" component="span" size="small" startIcon={<UploadIcon />}>
                            Upload OQ
                          </Button>
                        </label>
                      </>
                    )}
                  </Box>
                </Grid> */}

                {/* PQ Upload */}
                {/* <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>Upload PQ</Typography>
                    {editFormData.uploadPQ ? (
                      <Box>
                        <Button 
                          variant="outlined" 
                          size="small" 
                          onClick={() => handleFileView(editFormData.uploadPQ)}
                          sx={{ mr: 1 }}
                        >
                          View Current
                        </Button>
                        <input
                          accept=".pdf,.doc,.docx,.jpg,.png"
                          style={{ display: 'none' }}
                          id="replace-pq"
                          type="file"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handlePQUpload(file);
                            }
                          }}
                        />
                        <label htmlFor="replace-pq">
                          <Button variant="text" component="span" size="small">
                            Replace
                          </Button>
                        </label>
                        {editFormData.uploadPQOriginalName && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            File: {editFormData.uploadPQOriginalName}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <>
                        <input
                          accept=".pdf,.doc,.docx,.jpg,.png"
                          style={{ display: 'none' }}
                          id="upload-pq"
                          type="file"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handlePQUpload(file);
                            }
                          }}
                        />
                        <label htmlFor="upload-pq">
                          <Button variant="outlined" component="span" size="small" startIcon={<UploadIcon />}>
                            Upload PQ
                          </Button>
                        </label>
                      </>
                    )}
                  </Box>
                </Grid> */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="UID of Equipment"
                    value={editFormData.uid || ""}
                    onChange={(e) =>
                      setEditFormData((prev) => ({
                        ...prev,
                        uid: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Date of Installation"
                    type="date"
                    value={editFormData.dateOfInstallation || ""}
                    onChange={(e) =>
                      setEditFormData((prev) => ({
                        ...prev,
                        dateOfInstallation: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Limit of Detection"
                    value={editFormData.limitOfDetection || ""}
                    onChange={(e) =>
                      setEditFormData((prev) => ({
                        ...prev,
                        limitOfDetection: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseEditModal} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleUpdateEquipment}
            variant="contained"
            disabled={editLoading}
            startIcon={
              editLoading ? <CircularProgress size={20} /> : <SaveIcon />
            }
          >
            {editLoading ? "Updating..." : "Update Equipment"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DeleteIcon sx={{ color: "#d32f2f" }} />
            <Typography variant="h6">Confirm Delete</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            {deleteTarget === "single"
              ? "Are you sure you want to delete this equipment? This action cannot be undone."
              : `Are you sure you want to delete ${selectedForDelete.length} selected equipment(s)? This action cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={cancelDelete} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={existingEquipmentsLoading}
          >
            {existingEquipmentsLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Other Equipment Modal */}
      <Dialog
        open={addOtherModalOpen}
        onClose={handleCloseOtherModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, maxHeight: "90vh" },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            fontWeight: 600,
          }}
        >
          ➕ Add Other Equipment
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Equipment Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Name of Equipment"
                value={otherEquipmentForm.equipmentName || ""}
                onChange={(e) =>
                  handleOtherFormChange("equipmentName", e.target.value)
                }
                placeholder="Enter equipment name"
              />
            </Grid>

            {/* Manufacturer Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="make/company"
                value={otherEquipmentForm.manufacturerName}
                onChange={(e) =>
                  handleOtherFormChange("manufacturerName", e.target.value)
                }
                placeholder="make/company"
              />
            </Grid>

            {/* File Upload Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, color: "#7c3aed" }}>
                📁 File Uploads
              </Typography>
            </Grid>

            {/* Upload IQ */}
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Upload IQ
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block", mb: 1 }}
                >
                  (Allowed files Images/Pdf/word Max size: 30 MB)
                </Typography>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  style={{ display: "none" }}
                  id="iq-upload-other"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleOtherFileUpload(e.target.files[0], "iqFile");
                    }
                  }}
                />
                <label htmlFor="iq-upload-other">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    fullWidth
                    disabled={otherEquipmentLoading}
                  >
                    Choose File
                  </Button>
                </label>
                {otherEquipmentFiles.iqFile && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 2,
                      backgroundColor: "#f0fdf4",
                      borderRadius: 1,
                      border: "1px solid #bbf7d0",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "#16a34a", fontWeight: "medium" }}
                    >
                      ✅ File uploaded successfully
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#166534", display: "block" }}
                    >
                      📄 {otherEquipmentFiles.iqFile.fileName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#166534" }}>
                      💾 Will be saved when you click "Add Equipment"
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Upload OQ */}
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Upload OQ
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block", mb: 1 }}
                >
                  (Allowed files Images/Pdf/word Max size: 30 MB)
                </Typography>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  style={{ display: "none" }}
                  id="oq-upload-other"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleOtherFileUpload(e.target.files[0], "oqFile");
                    }
                  }}
                />
                <label htmlFor="oq-upload-other">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    fullWidth
                    disabled={otherEquipmentLoading}
                  >
                    Choose File
                  </Button>
                </label>
                {otherEquipmentFiles.oqFile && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 2,
                      backgroundColor: "#f0fdf4",
                      borderRadius: 1,
                      border: "1px solid #bbf7d0",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "#16a34a", fontWeight: "medium" }}
                    >
                      ✅ File uploaded successfully
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#166534", display: "block" }}
                    >
                      📄 {otherEquipmentFiles.oqFile.fileName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#166534" }}>
                      💾 Will be saved when you click "Add Equipment"
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Upload PQ */}
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Upload PQ *
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block", mb: 1 }}
                >
                  (Allowed files Images/Pdf/word Max size: 30 MB)
                </Typography>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  style={{ display: "none" }}
                  id="pq-upload-other"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleOtherFileUpload(e.target.files[0], "pqFile");
                    }
                  }}
                />
                <label htmlFor="pq-upload-other">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    fullWidth
                    disabled={otherEquipmentLoading}
                  >
                    Choose File
                  </Button>
                </label>
                {otherEquipmentFiles.pqFile && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 2,
                      backgroundColor: "#f0fdf4",
                      borderRadius: 1,
                      border: "1px solid #bbf7d0",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "#16a34a", fontWeight: "medium" }}
                    >
                      ✅ File uploaded successfully
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#166534", display: "block" }}
                    >
                      📄 {otherEquipmentFiles.pqFile.fileName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#166534" }}>
                      💾 Will be saved when you click "Add Equipment"
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Equipment Details Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, color: "#1976d2" }}>
                🔧 Equipment Details
              </Typography>
            </Grid>

            {/* UID of Equipment */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="UID of Equipment"
                value={otherEquipmentForm.uid}
                onChange={(e) => handleOtherFormChange("uid", e.target.value)}
                placeholder="UID Of Equipment"
                helperText="UID:- Unique Identification number of equipment"
              />
            </Grid>

            {/* Serial No */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Serial No"
                value={otherEquipmentForm.serialNo}
                onChange={(e) =>
                  handleOtherFormChange("serialNo", e.target.value)
                }
                placeholder="Serial No"
              />
            </Grid>

            {/* Model */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Model"
                value={otherEquipmentForm.model}
                onChange={(e) => handleOtherFormChange("model", e.target.value)}
                placeholder="Model"
              />
            </Grid>

            {/* Year of Make */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="Year of Make"
                value={otherEquipmentForm.yearOfMake}
                onChange={(e) =>
                  handleOtherFormChange("yearOfMake", e.target.value)
                }
                inputProps={{ min: 1900, max: new Date().getFullYear() + 1 }}
              />
            </Grid>

            {/* Receipt Date */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Receipt date"
                value={otherEquipmentForm.receiptDate}
                onChange={(e) =>
                  handleOtherFormChange("receiptDate", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
                helperText="Reciept Date"
              />
            </Grid>

            {/* Date of Installation */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Date of Installation"
                value={otherEquipmentForm.dateOfInstallation}
                onChange={(e) =>
                  handleOtherFormChange("dateOfInstallation", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Limit of Detection */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Limit of Detection"
                value={otherEquipmentForm.limitOfDetection}
                onChange={(e) =>
                  handleOtherFormChange("limitOfDetection", e.target.value)
                }
                placeholder="Limit of Detection"
              />
            </Grid>

            {/* Date of last Calibration */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Date of last Calibration"
                value={otherEquipmentForm.dateOfLastCalibration}
                onChange={(e) =>
                  handleOtherFormChange("dateOfLastCalibration", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: todayDate, // Last calibration cannot be in the future
                }}
                helperText="Calibration Date"
              />
            </Grid>

            {/* Calibration Due on */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                type="date"
                label="Calibration Due on"
                value={otherEquipmentForm.calibrationDueOn}
                onChange={(e) =>
                  handleOtherFormChange("calibrationDueOn", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: todayDate,
                }}
                helperText="The laboratory to decide the calibration interval based on NABL 112/112A & ISO 10012 or ILAC-G24"
              />
            </Grid>

            {/* Calibrated by */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Calibrated by *
              </Typography>
              <RadioGroup
                row
                value={otherEquipmentForm.calibratedBy}
                onChange={(e) =>
                  handleOtherFormChange("calibratedBy", e.target.value)
                }
              >
                <FormControlLabel
                  value="inhouse"
                  control={<Radio />}
                  label="Inhouse"
                />
                <FormControlLabel
                  value="external"
                  control={<Radio />}
                  label="External"
                />
              </RadioGroup>
            </Grid>

            {/* Conditional Fields based on Calibrated by */}
            {otherEquipmentForm.calibratedBy === "inhouse" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Traceability"
                  value={otherEquipmentForm.traceability}
                  onChange={(e) =>
                    handleOtherFormChange("traceability", e.target.value)
                  }
                  placeholder="Traceability"
                />
              </Grid>
            )}

            {otherEquipmentForm.calibratedBy === "external" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Name of Calibration Agency"
                  value={otherEquipmentForm.calibrationAgencyName}
                  onChange={(e) =>
                    handleOtherFormChange(
                      "calibrationAgencyName",
                      e.target.value,
                    )
                  }
                  placeholder="Name of Calibration Agency"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleCloseOtherModal}
            startIcon={<CancelIcon />}
            disabled={otherEquipmentLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitOtherEquipment}
            startIcon={
              otherEquipmentLoading ? (
                <CircularProgress size={20} />
              ) : (
                <SaveIcon />
              )
            }
            disabled={otherEquipmentLoading}
            sx={{
              minWidth: 200,
            }}
          >
            {otherEquipmentLoading
              ? "Adding Equipment..."
              : `Add Equipment ${
                  Object.values(otherEquipmentFiles).filter(
                    (file) => file !== null,
                  ).length > 0
                    ? `(${Object.values(otherEquipmentFiles).filter((file) => file !== null).length} files ready)`
                    : ""
                }`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Upload Preview Modal */}
      <Dialog
        open={previewModalOpen}
        onClose={handleClosePreviewModal}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { height: "90vh" },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ color: "#1976d2" }}>
                📋 Bulk Upload Preview & Edit
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                File: {previewFileName} | Rows: {editedPreviewData.length}
                {hasUnsavedChanges && (
                  <Typography component="span" sx={{ color: "orange", ml: 1 }}>
                    • Unsaved changes
                  </Typography>
                )}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                onClick={handleSaveEdit}
                variant="contained"
                size="small"
                startIcon={<SaveIcon />}
                disabled={!hasUnsavedChanges}
                sx={{ bgcolor: "#2e7d32", "&:hover": { bgcolor: "#1b5e20" } }}
              >
                Save Changes
              </Button>
              <Button
                onClick={handleClosePreviewModal}
                variant="outlined"
                size="small"
              >
                ✕ Close
              </Button>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: 2,
              backgroundColor: "#e3f2fd",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#1565c0", fontWeight: "medium" }}
            >
              💡 Click on any cell to edit its value. Date columns (📅) use date
              picker inputs.
              <strong>Dropdown columns (📋) provide predefined options</strong>.
              <strong>Locked columns (🔒) are read-only</strong> and cannot be
              edited.
              <strong> Required fields (*) must be filled</strong> before
              saving. Invalid or missing required fields will be highlighted in
              red with specific error messages. Remember to save your changes
              before uploading.
            </Typography>
          </Box>

          {/* Validation Error Summary */}
          {Object.keys(validationErrors).length > 0 && (
            <Box
              sx={{
                p: 2,
                backgroundColor: "#ffebee",
                borderBottom: "1px solid #f44336",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#d32f2f", fontWeight: "medium", mb: 1 }}
              >
                ⚠️ Validation Errors Found (
                {Object.keys(validationErrors).reduce(
                  (total, rowIndex) =>
                    total + Object.keys(validationErrors[rowIndex]).length,
                  0,
                )}{" "}
                errors across {Object.keys(validationErrors).length} rows):
              </Typography>
              <Box sx={{ maxHeight: "100px", overflow: "auto" }}>
                {Object.entries(validationErrors).map(
                  ([rowIndex, rowErrors]) => (
                    <Box key={rowIndex} sx={{ mb: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: "#d32f2f", fontWeight: "bold" }}
                      >
                        Row {parseInt(rowIndex) + 1}:
                      </Typography>
                      {Object.entries(rowErrors).map(([colIndex, errorMsg]) => (
                        <Typography
                          key={colIndex}
                          variant="caption"
                          sx={{ color: "#d32f2f", display: "block", ml: 2 }}
                        >
                          • {previewHeaders[colIndex]}: {errorMsg}
                        </Typography>
                      ))}
                    </Box>
                  ),
                )}
              </Box>
            </Box>
          )}

          {previewData.length > 0 ? (
            <Box sx={{ height: "100%", overflow: "auto" }}>
              <TableContainer component={Paper} sx={{ maxHeight: "60vh" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          backgroundColor: "#f5f5f5",
                          minWidth: 60,
                        }}
                      >
                        Row #
                      </TableCell>
                      {previewHeaders.map((header, index) => (
                        <TableCell
                          key={index}
                          sx={{
                            fontWeight: 600,
                            backgroundColor: isNonEditableColumn(header)
                              ? "#e0e0e0"
                              : isDateColumn(header)
                                ? "#e3f2fd"
                                : "#f5f5f5",
                            minWidth: 150,
                            border: "1px solid #e0e0e0",
                            color: isNonEditableColumn(header)
                              ? "#666666"
                              : isDateColumn(header)
                                ? "#1565c0"
                                : "inherit",
                            position: "relative",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                            }}
                          >
                            <span>
                              {isNonEditableColumn(header)
                                ? "🔒 "
                                : isDropdownColumn(header)
                                  ? "📋 "
                                  : isDateColumn(header)
                                    ? "📅 "
                                    : ""}
                              {header || `Column ${index + 1}`}
                              {isRequiredColumn(header) && (
                                <Typography
                                  component="span"
                                  sx={{
                                    color: "#d32f2f",
                                    fontWeight: "bold",
                                    ml: 0.5,
                                  }}
                                >
                                  *
                                </Typography>
                              )}
                            </span>
                          </Box>
                          {isNonEditableColumn(header) && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                fontSize: "0.7rem",
                                color: "#888888",
                                fontStyle: "italic",
                              }}
                            >
                              (Read-only)
                            </Typography>
                          )}
                          {isDropdownColumn(header) && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                fontSize: "0.7rem",
                                color: "#1976d2",
                                fontStyle: "italic",
                              }}
                            >
                              (Dropdown)
                            </Typography>
                          )}
                          {isRequiredColumn(header) &&
                            !isNonEditableColumn(header) &&
                            !isDropdownColumn(header) && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  fontSize: "0.7rem",
                                  color: "#d32f2f",
                                  fontStyle: "italic",
                                }}
                              >
                                (Required)
                              </Typography>
                            )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {editedPreviewData.slice(0, 100).map((row, rowIndex) => (
                      <TableRow
                        key={rowIndex}
                        sx={{
                          backgroundColor:
                            rowIndex % 2 === 0 ? "#ffffff" : "#f8fafc",
                          "&:hover": { backgroundColor: "#fff3e0" },
                        }}
                      >
                        <TableCell
                          sx={{ fontWeight: "bold", color: "#1976d2" }}
                        >
                          {rowIndex + 1}
                        </TableCell>
                        {previewHeaders.map((_, colIndex) => {
                          const hasError =
                            validationErrors[rowIndex] &&
                            validationErrors[rowIndex][colIndex];
                          const errorMessage = hasError
                            ? validationErrors[rowIndex][colIndex]
                            : "";
                          const isRequired = isRequiredColumn(
                            previewHeaders[colIndex],
                          );

                          return (
                            <TableCell
                              key={colIndex}
                              sx={{
                                border: "1px solid #e0e0e0",
                                maxWidth: 200,
                                padding: "4px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                backgroundColor: isNonEditableColumn(
                                  previewHeaders[colIndex],
                                )
                                  ? "#f5f5f5"
                                  : hasError
                                    ? "#ffebee"
                                    : "inherit",
                                borderColor: hasError ? "#f44336" : "#e0e0e0",
                                borderWidth: hasError ? "2px" : "1px",
                              }}
                              title={row[colIndex] || ""}
                            >
                              {isDropdownColumn(previewHeaders[colIndex]) ? (
                                <FormControl
                                  size="small"
                                  fullWidth
                                  error={!!hasError}
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      fontSize: "0.875rem",
                                      minHeight: "auto",
                                    },
                                  }}
                                >
                                  <Select
                                    value={row[colIndex] || ""}
                                    onChange={(e) =>
                                      handleCellValueChange(
                                        rowIndex,
                                        colIndex,
                                        e.target.value,
                                      )
                                    }
                                    displayEmpty
                                    variant="outlined"
                                    sx={{
                                      "& .MuiSelect-select": {
                                        padding: "6px 8px",
                                      },
                                    }}
                                  >
                                    <MenuItem value="">
                                      <em>Select...</em>
                                    </MenuItem>
                                    <MenuItem value="In-house">
                                      In-house
                                    </MenuItem>
                                    <MenuItem value="External">
                                      External
                                    </MenuItem>
                                  </Select>
                                  {hasError && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#d32f2f",
                                        fontSize: "0.7rem",
                                        mt: 0.5,
                                      }}
                                    >
                                      {errorMessage}
                                    </Typography>
                                  )}
                                  {!hasError && isRequired && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#666",
                                        fontSize: "0.7rem",
                                        mt: 0.5,
                                      }}
                                    >
                                      Required *
                                    </Typography>
                                  )}
                                </FormControl>
                              ) : (
                                <TextField
                                  value={
                                    isDateColumn(previewHeaders[colIndex])
                                      ? formatDateForInput(row[colIndex])
                                      : row[colIndex] || ""
                                  }
                                  onChange={(e) =>
                                    handleCellValueChange(
                                      rowIndex,
                                      colIndex,
                                      e.target.value,
                                    )
                                  }
                                  type={
                                    isDateColumn(previewHeaders[colIndex])
                                      ? "date"
                                      : "text"
                                  }
                                  size="small"
                                  variant="outlined"
                                  fullWidth
                                  disabled={isNonEditableColumn(
                                    previewHeaders[colIndex],
                                  )}
                                  error={!!hasError}
                                  helperText={
                                    hasError
                                      ? errorMessage
                                      : isRequired
                                        ? "Required *"
                                        : ""
                                  }
                                  InputLabelProps={
                                    isDateColumn(previewHeaders[colIndex])
                                      ? { shrink: true }
                                      : undefined
                                  }
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      fontSize: "0.875rem",
                                      minHeight: "auto",
                                    },
                                    "& .MuiInputBase-input": {
                                      padding: "6px 8px",
                                    },
                                    "& .MuiFormHelperText-root": {
                                      fontSize: "0.7rem",
                                      margin: "2px 0 0 0",
                                      lineHeight: 1.2,
                                    },
                                  }}
                                  InputProps={{
                                    readOnly: isNonEditableColumn(
                                      previewHeaders[colIndex],
                                    ),
                                    sx: {
                                      backgroundColor: isNonEditableColumn(
                                        previewHeaders[colIndex],
                                      )
                                        ? "#f5f5f5"
                                        : hasError
                                          ? "#ffebee"
                                          : isDateColumn(
                                                previewHeaders[colIndex],
                                              )
                                            ? "#f0f8ff"
                                            : "white",
                                      "&:hover": {
                                        backgroundColor: isNonEditableColumn(
                                          previewHeaders[colIndex],
                                        )
                                          ? "#f5f5f5"
                                          : hasError
                                            ? "#ffcdd2"
                                            : isDateColumn(
                                                  previewHeaders[colIndex],
                                                )
                                              ? "#e6f3ff"
                                              : "#f5f5f5",
                                      },
                                      "&.Mui-focused": {
                                        backgroundColor: isNonEditableColumn(
                                          previewHeaders[colIndex],
                                        )
                                          ? "#f5f5f5"
                                          : hasError
                                            ? "#ffebee"
                                            : isDateColumn(
                                                  previewHeaders[colIndex],
                                                )
                                              ? "#f0f8ff"
                                              : "white",
                                      },
                                      "&.Mui-disabled": {
                                        backgroundColor: "#f5f5f5",
                                        color: "#666666",
                                      },
                                      "&.Mui-error": {
                                        borderColor: "#f44336",
                                      },
                                    },
                                  }}
                                />
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {editedPreviewData.length > 100 && (
                <Box
                  sx={{ p: 2, backgroundColor: "#fff3cd", textAlign: "center" }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Showing first 100 rows. Total rows:{" "}
                    {editedPreviewData.length}
                    <Typography
                      component="span"
                      sx={{ color: "#ed6c02", ml: 1 }}
                    >
                      • You can only edit the first 100 rows in preview
                    </Typography>
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">No data to preview</Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, backgroundColor: "#f8fafc" }}>
          {hasUnsavedChanges && (
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="body2"
                sx={{ color: "orange", fontWeight: "medium" }}
              >
                ⚠️ You have unsaved changes. Save your changes before uploading.
              </Typography>
            </Box>
          )}
          <Button
            onClick={handleClosePreviewModal}
            variant="outlined"
            startIcon={<CancelIcon />}
            disabled={hasUnsavedChanges}
          >
            {hasUnsavedChanges ? "Save Changes First" : "Cancel"}
          </Button>
          {(applicationStage === null ||
            applicationStage === "" ||
            applicationStage === "18a" ||
            isEquipmentSectionUnfrozen) && (
            <Button
              onClick={handleConfirmUpload}
              variant="contained"
              disabled={
                editedPreviewData.length === 0 || loading || hasUnsavedChanges
              }
              startIcon={
                loading ? <CircularProgress size={20} /> : <SaveIcon />
              }
              sx={{ minWidth: 200 }}
            >
              {loading
                ? "Uploading..."
                : hasUnsavedChanges
                  ? "Save Changes First"
                  : `Confirm Upload (${editedPreviewData.length} rows)`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={closeNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={closeNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Edit Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Bulk Adequacy Check Modal */}
      <Dialog
        open={bulkAdequacyModalOpen}
        onClose={handleCloseBulkAdequacyModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: "#1976d2",
            color: "white",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CheckCircleIcon />
          Bulk Adequacy Check for Equipment
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {/* Selected Equipment Names Display */}
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Marking adequacy status for {selectedAdequacyEquipments.length}{" "}
              selected equipment(s):
            </Typography>
          </Alert>

          {/* Adequacy Status Selection */}
          <FormControl fullWidth margin="normal">
            <InputLabel required>Adequacy Status</InputLabel>
            <Select
              value={bulkAdequacyStatus}
              onChange={(e) => setBulkAdequacyStatus(e.target.value)}
              label="Adequacy Status"
              required
            >
              <MenuItem value="Adequate">✅ Adequate</MenuItem>
              <MenuItem value="Inadequate">❌ Inadequate</MenuItem>
              {userTypeId === "4" && (
                <MenuItem value="To be Verify">
                  <VerifyIcon sx={{ color: "#35dcdcff", fontSize: "1rem" }} />
                  &nbsp;To be Verify
                </MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Conditional Clause Number Field - Only show for Assessment users when Inadequate is selected */}
          {bulkAdequacyStatus === "Inadequate" && userTypeId === "6" && (
            <FormControl fullWidth margin="normal">
              <Autocomplete
                fullWidth
                options={clauseNo}
                value={selectedClause}
                onChange={(event, newValue) => {
                  setSelectedClause(newValue);
                }}
                getOptionLabel={(option) => {
                  // Handle both old and new clause object structures
                  const clauseNo = option.DisplayName || option.ClauseNo || "";
                  const description =
                    option.ClauseDescription || option.Description || "";
                  return description
                    ? `${clauseNo} - ${description}`
                    : clauseNo;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Clause No."
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        borderRadius: 2,
                        "&:hover": {
                          boxShadow: "0 2px 8px rgba(0,123,255,0.15)",
                        },
                        "&.Mui-focused": {
                          boxShadow: "0 4px 12px rgba(0,123,255,0.25)",
                        },
                      },
                    }}
                  />
                )}
              />
            </FormControl>
          )}

          {/* Conditional Remarks Field - Only show when Inadequate is selected */}
          {bulkAdequacyStatus === "Inadequate" && (
            <>
              {/* Simple textarea for remarks */}
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Remarks (Required)"
                placeholder="Type your detailed remarks here. Use the dropdown below to select predefined remarks."
                value={bulkAdequacyRemarks || ""}
                onChange={(e) =>
                  handleBulkAdequacyRemarksChange(e.target.value, false)
                }
                required
                margin="normal"
                variant="outlined"
                error={
                  bulkAdequacyStatus === "Inadequate" &&
                  !bulkAdequacyRemarks?.trim()
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                    borderRadius: 2,
                    "&:hover": {
                      boxShadow: "0 2px 8px rgba(220,53,69,0.15)",
                    },
                    "&.Mui-focused": {
                      boxShadow: "0 4px 12px rgba(220,53,69,0.25)",
                    },
                    "&.Mui-error": {
                      "& fieldset": {
                        borderColor: "#d32f2f",
                      },
                    },
                  },
                }}
              />

              {/* Predefined Remarks Dropdown */}
              {bulkScrutinyRemarks.length > 0 && (
                <Box sx={{ mt: 2, position: "relative" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      setIsBulkCheckboxDropdownOpen(!isBulkCheckboxDropdownOpen)
                    }
                    sx={{
                      mb: 1,
                      borderColor: "#007bff",
                      color: "#007bff",
                      "&:hover": {
                        borderColor: "#0056b3",
                        backgroundColor: "#f8f9ff",
                      },
                    }}
                  >
                    {isBulkCheckboxDropdownOpen ? "Hide" : "Show"} Predefined
                    Remarks ({bulkScrutinyRemarks.length} available)
                  </Button>

                  {selectedBulkRemarksOptions.length > 0 && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleClearAllBulkCheckboxes}
                      sx={{ ml: 1, color: "#dc3545" }}
                    >
                      Clear All ({selectedBulkRemarksOptions.length})
                    </Button>
                  )}

                  {isBulkCheckboxDropdownOpen && (
                    <Paper
                      elevation={3}
                      sx={{
                        mt: 1,
                        maxHeight: 300,
                        overflow: "auto",
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                      }}
                    >
                      <MenuList dense>
                        {bulkScrutinyRemarks.map((option, index) => {
                          const remarkText =
                            typeof option === "string"
                              ? option
                              : option.inadequate_remarks;
                          const isSelected = selectedBulkRemarksOptions.some(
                            (item) =>
                              (typeof item === "string"
                                ? item
                                : item.inadequate_remarks) === remarkText,
                          );

                          return (
                            <MenuItem
                              key={index}
                              onClick={() =>
                                handleBulkCheckboxChange(option, !isSelected)
                              }
                              sx={{
                                py: 1,
                                px: 2,
                                display: "flex",
                                alignItems: "flex-start",
                                minHeight: "auto",
                              }}
                            >
                              <Checkbox
                                checked={isSelected}
                                size="small"
                                sx={{ p: 0, mr: 2 }}
                              />
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "0.875rem",
                                    lineHeight: 1.4,
                                    wordBreak: "break-word",
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  {remarkText}
                                </Typography>
                                {typeof option !== "string" && option.type && (
                                  <Chip
                                    label={option.type}
                                    size="small"
                                    color={
                                      option.type === "Assessment"
                                        ? "primary"
                                        : "secondary"
                                    }
                                    variant="outlined"
                                    sx={{
                                      fontSize: "0.7rem",
                                      height: "18px",
                                      mt: 0.5,
                                    }}
                                  />
                                )}
                              </Box>
                            </MenuItem>
                          );
                        })}
                      </MenuList>
                    </Paper>
                  )}
                </Box>
              )}

              <Typography
                variant="caption"
                sx={{
                  color: "#6c757d",
                  display: "block",
                  mt: 1,
                  fontStyle: "italic",
                }}
              >
                {bulkLoadingRemarks
                  ? "Loading existing remarks..."
                  : bulkScrutinyRemarks.length > 0
                    ? `${bulkScrutinyRemarks.length} predefined remark(s) available for "Equipment". You can select multiple options from the dropdown above.`
                    : "Detailed remarks are mandatory when marking equipment as inadequate."}
              </Typography>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
          <Button
            onClick={handleCloseBulkAdequacyModal}
            disabled={bulkAdequacyLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkAdequacySubmit}
            variant="contained"
            disabled={
              !bulkAdequacyStatus ||
              (bulkAdequacyStatus === "Inadequate" &&
                !bulkAdequacyRemarks?.trim()) ||
              bulkAdequacyLoading
            }
            startIcon={
              bulkAdequacyLoading ? (
                <CircularProgress size={20} />
              ) : (
                <CheckCircleIcon />
              )
            }
            sx={{
              minWidth: 180,
              backgroundColor:
                bulkAdequacyStatus === "Adequate" ? "#28a745" : "#dc3545",
              "&:hover": {
                backgroundColor:
                  bulkAdequacyStatus === "Adequate" ? "#218838" : "#c82333",
              },
              "&.Mui-disabled": {
                backgroundColor: "#cccccc",
              },
            }}
          >
            {bulkAdequacyLoading
              ? "Submitting..."
              : `Mark as ${bulkAdequacyStatus || "Status"} (${selectedAdequacyEquipments.length})`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MedicalEquipmentSection;